import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Header from "../home/components/Header";
import Footer from "../home/components/Footer";
import MobileStickyTopBar from "../../components/feature/MobileStickyTopBar";

interface CityData {
  name: string;
  population: string;
  county: string;
  zipCodes: string[];
  description: string;
  neighborhoods: string[];
  localInsights: string[];
  faqs: { question: string; answer: string }[];
}

const cityGeo: Record<string, { lat: number; lng: number }> = {
  carmel: { lat: 39.9784, lng: -86.118 },
  fishers: { lat: 39.9568, lng: -86.0139 },
  westfield: { lat: 40.0428, lng: -86.1276 },
  noblesville: { lat: 40.0456, lng: -86.0086 },
  indianapolis: { lat: 39.7684, lng: -86.1581 },
  brownsburg: { lat: 39.8434, lng: -86.3956 },
  zionsville: { lat: 39.9509, lng: -86.2619 },
  whitestown: { lat: 39.9967, lng: -86.3456 },
  lebanon: { lat: 40.0483, lng: -86.4694 },
  mooresville: { lat: 39.6128, lng: -86.3739 },
  castleton: { lat: 39.9012, lng: -86.0639 },
  geist: { lat: 39.9201, lng: -85.9897 },
  "broad-ripple": { lat: 39.8676, lng: -86.1422 },
  greenwood: { lat: 39.6134, lng: -86.1067 },
  avon: { lat: 39.7631, lng: -86.3994 },
  plainfield: { lat: 39.7042, lng: -86.3994 },
  lawrence: { lat: 39.8384, lng: -86.0253 },
  "beech-grove": { lat: 39.7231, lng: -86.0894 },
  speedway: { lat: 39.7917, lng: -86.2478 },
  anderson: { lat: 40.1053, lng: -85.6803 },
  muncie: { lat: 40.1934, lng: -85.3864 },
  columbus: { lat: 39.2014, lng: -85.9214 },
  bloomington: { lat: 39.1653, lng: -86.5264 },
  "terre-haute": { lat: 39.4667, lng: -87.4139 },
  kokomo: { lat: 40.4864, lng: -86.1336 },
  richmond: { lat: 39.8289, lng: -84.8903 },
  "fort-wayne": { lat: 41.0793, lng: -85.1394 },
  franklin: { lat: 39.4806, lng: -86.0547 },
  cumberland: { lat: 39.7742, lng: -85.9597 },
  greenfield: { lat: 39.7856, lng: -85.7697 },
  danville: { lat: 39.7606, lng: -86.5253 },
  martinsville: { lat: 39.4281, lng: -86.4283 },
};

// Nearby cities mapping for internal linking
const nearbyCities: Record<string, Array<{ slug: string; name: string }>> = {
  carmel: [
    { slug: "fishers", name: "Fishers" },
    { slug: "westfield", name: "Westfield" },
    { slug: "noblesville", name: "Noblesville" },
    { slug: "zionsville", name: "Zionsville" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  fishers: [
    { slug: "carmel", name: "Carmel" },
    { slug: "noblesville", name: "Noblesville" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "geist", name: "Geist" },
    { slug: "lawrence", name: "Lawrence" },
  ],
  indianapolis: [
    { slug: "carmel", name: "Carmel" },
    { slug: "fishers", name: "Fishers" },
    { slug: "noblesville", name: "Noblesville" },
    { slug: "westfield", name: "Westfield" },
    { slug: "greenwood", name: "Greenwood" },
  ],
  westfield: [
    { slug: "carmel", name: "Carmel" },
    { slug: "noblesville", name: "Noblesville" },
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "whitestown", name: "Whitestown" },
  ],
  noblesville: [
    { slug: "carmel", name: "Carmel" },
    { slug: "fishers", name: "Fishers" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "westfield", name: "Westfield" },
    { slug: "anderson", name: "Anderson" },
  ],
  brownsburg: [
    { slug: "zionsville", name: "Zionsville" },
    { slug: "avon", name: "Avon" },
    { slug: "plainfield", name: "Plainfield" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "whitestown", name: "Whitestown" },
  ],
  zionsville: [
    { slug: "carmel", name: "Carmel" },
    { slug: "westfield", name: "Westfield" },
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "whitestown", name: "Whitestown" },
  ],
  whitestown: [
    { slug: "zionsville", name: "Zionsville" },
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "lebanon", name: "Lebanon" },
    { slug: "westfield", name: "Westfield" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  lebanon: [
    { slug: "whitestown", name: "Whitestown" },
    { slug: "zionsville", name: "Zionsville" },
  ],
  mooresville: [
    { slug: "plainfield", name: "Plainfield" },
    { slug: "avon", name: "Avon" },
    { slug: "castleton", name: "Castleton" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  castleton: [
    { slug: "fishers", name: "Fishers" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "indianapolis", name: "Lawrence" },
    { slug: "geist", name: "Geist" },
    { slug: "noblesville", name: "Noblesville" },
  ],
  geist: [
    { slug: "fishers", name: "Fishers" },
    { slug: "castleton", name: "Castleton" },
    { slug: "lawrence", name: "Lawrence" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "noblesville", name: "Noblesville" },
  ],
  "broad-ripple": [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "castleton", name: "Castleton" },
    { slug: "lawrence", name: "Lawrence" },
    { slug: "carmel", name: "Carmel" },
  ],
  greenwood: [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "mooresville", name: "Mooresville" },
    { slug: "avon", name: "Avon" },
    { slug: "castleton", name: "Castleton" },
  ],
  avon: [
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "plainfield", name: "Plainfield" },
    { slug: "mooresville", name: "Mooresville" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "whitestown", name: "Whitestown" },
  ],
  plainfield: [
    { slug: "avon", name: "Avon" },
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "mooresville", name: "Mooresville" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  lawrence: [
    { slug: "fishers", name: "Fishers" },
    { slug: "castleton", name: "Castleton" },
    { slug: "geist", name: "Geist" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "beech-grove", name: "Beech Grove" },
  ],
  "beech-grove": [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "greenwood", name: "Greenwood" },
    { slug: "lawrence", name: "Lawrence" },
    { slug: "speedway", name: "Speedway" },
  ],
  speedway: [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "avon", name: "Avon" },
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "beech-grove", name: "Beech Grove" },
  ],
  anderson: [
    { slug: "noblesville", name: "Noblesville" },
    { slug: "fishers", name: "Fishers" },
    { slug: "muncie", name: "Muncie" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  muncie: [
    { slug: "anderson", name: "Anderson" },
    { slug: "kokomo", name: "Kokomo" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  columbus: [
    { slug: "greenwood", name: "Greenwood" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "bloomington", name: "Bloomington" },
  ],
  bloomington: [
    { slug: "columbus", name: "Columbus" },
    { slug: "terre-haute", name: "Terre Haute" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  "terre-haute": [
    { slug: "bloomington", name: "Bloomington" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  kokomo: [
    { slug: "muncie", name: "Muncie" },
    { slug: "anderson", name: "Anderson" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  richmond: [
    { slug: "muncie", name: "Muncie" },
    { slug: "anderson", name: "Anderson" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  "fort-wayne": [
    { slug: "kokomo", name: "Kokomo" },
    { slug: "muncie", name: "Muncie" },
    { slug: "anderson", name: "Anderson" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  franklin: [
    { slug: "greenwood", name: "Greenwood" },
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "martinsville", name: "Martinsville" },
    { slug: "columbus", name: "Columbus" },
  ],
  cumberland: [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "greenfield", name: "Greenfield" },
    { slug: "lawrence", name: "Lawrence" },
    { slug: "beech-grove", name: "Beech Grove" },
  ],
  greenfield: [
    { slug: "indianapolis", name: "Indianapolis" },
    { slug: "cumberland", name: "Cumberland" },
    { slug: "anderson", name: "Anderson" },
    { slug: "muncie", name: "Muncie" },
  ],
  danville: [
    { slug: "brownsburg", name: "Brownsburg" },
    { slug: "avon", name: "Avon" },
    { slug: "plainfield", name: "Plainfield" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
  martinsville: [
    { slug: "mooresville", name: "Mooresville" },
    { slug: "greenwood", name: "Greenwood" },
    { slug: "bloomington", name: "Bloomington" },
    { slug: "indianapolis", name: "Indianapolis" },
  ],
};

// City-specific offer variants for top 5 cities (local SEO headline boost)
const cityOffers: Record<string, {
  badge: string;
  headline: string;
  subheadline: string;
  offer: string;
  offerDetail: string;
  highlight: string;
}> = {
  carmel: {
    badge: "Hamilton County\'s #1 Rated Agent",
    headline: "Carmel\'s Premier Life Insurance Specialist",
    subheadline: "Protecting $450K+ Homes & High-Income Carmel Families",
    offer: "Free Wealth Strategy Session — A $250 Value",
    offerDetail: "Book before March 31 and receive a complimentary 30-minute retirement income analysis",
    highlight: "Trusted by 200+ Carmel families in West Clay, Arts District & Meridian Corridor",
  },
  fishers: {
    badge: "Fishers Families Trust Us",
    headline: "Fishers\' Most Trusted Life Insurance Advisor",
    subheadline: "Protecting HSE Families, Tech Professionals & Saxony Homeowners",
    offer: "Free Income Protection Analysis for Fishers Families",
    offerDetail: "We compare 20+ carriers to find rates Fishers families save an average of 23% vs. going direct",
    highlight: "Serving Fishers families near Saxony, Geist & the Nickel Plate District",
  },
  noblesville: {
    badge: "Noblesville\'s Local Choice",
    headline: "Life Insurance in Noblesville — Local, Personal, Affordable",
    subheadline: "From Morse Reservoir to Hazel Dell — We\'ve Got You Covered",
    offer: "Free Coverage Gap Analysis for Noblesville Homeowners",
    offerDetail: "Many Noblesville families are underinsured — our free analysis shows exactly what you\'re missing",
    highlight: "Fast approvals for Noblesville families — many clients approved same day",
  },
  westfield: {
    badge: "Grand Park Area Specialist",
    headline: "Westfield\'s #1 Growing-Family Insurance Specialist",
    subheadline: "Indiana\'s Fastest-Growing City Deserves the Best Protection",
    offer: "New Homeowner Rate Lock for Westfield Families",
    offerDetail: "Lock in today\'s rates before Westfield home values — and premiums — rise even higher",
    highlight: "Serving Chatham Hills, Harmony, Bridgewater Club & all Westfield neighborhoods",
  },
  greenwood: {
    badge: "Johnson County Trusted",
    headline: "Greenwood Life Insurance — Affordable, Local & Independent",
    subheadline: "Protecting Center Grove & Greenwood Families the Right Way",
    offer: "Free Family Protection Review — No Cost, No Pressure",
    offerDetail: "Greenwood families save an average of 23% when we compare multiple carriers on their behalf",
    highlight: "Serving Stones Crossing, Smith Valley Road & all Greenwood neighborhoods",
  },
};

const cityData: Record<string, CityData> = {
  carmel: {
    name: "Carmel",
    population: "102,000+",
    county: "Hamilton County",
    zipCodes: ["46032", "46033", "46074", "46280", "46290"],
    description: "Carmel is one of the fastest-growing and most affluent communities in Indiana, known for its award-winning Arts & Design District, top-rated schools, and family-friendly neighborhoods. With a median household income exceeding $113,000 and a homeownership rate above 80%, Carmel families need robust life insurance protection to safeguard their mortgages, children's education, and long-term financial security.",
    neighborhoods: [
      "Arts & Design District",
      "Meridian Corridor",
      "West Clay",
      "Village of WestClay",
      "Woodland Golf Club",
      "Crooked Stick",
      "Brookshire",
      "Smokey Ridge",
      "Hayden Run",
      "Spring Mill Ponds",
      "Overbrook Farms",
      "Clay Terrace Area",
    ],
    localInsights: [
      "Median household income: $113,000+ (well above Indiana average)",
      "Homeownership rate: 82% — most families carry significant mortgage debt",
      "Top employers: Salesforce, Allegion, KAR Global, CNO Financial, IU Health North",
      "Ranked #1 Best Place to Live in Indiana by Money Magazine (2018, 2020)",
      "Average home value: $450,000+ — mortgage protection is critical",
      "Hamilton County has the highest median income in Indiana",
      "Carmel Clay Schools rated among top 10 districts in the state",
    ],
    faqs: [
      {
        question: "How much life insurance do Carmel families typically need?",
        answer: "Most Carmel families need $500,000–$1,500,000 in coverage to fully protect their mortgage (average $350,000+), replace lost income, cover children's college expenses, and maintain their standard of living. With median household incomes above $113,000, a good rule of thumb is 10–12x your annual income. We provide a free Financial Needs Analysis to calculate your exact number based on your mortgage, debts, and family goals.",
      },
      {
        question: "What's the average cost of life insurance for a Carmel resident?",
        answer: "A healthy 35-year-old in Carmel can get $500,000 of 20-year term life insurance for $25–$35/month. A 45-year-old might pay $60–$90/month for the same coverage. Rates depend on age, health, coverage amount, and term length. Because we're independent and shop across dozens of carriers, we find the most competitive rates for your specific profile — often saving Carmel families 20–30% compared to going direct to a single carrier.",
      },
      {
        question: "Do you serve Carmel residents by phone or video, or do I need to visit an office?",
        answer: "We serve Carmel families by phone consultation, video conference (Zoom/Google Meet), or in-home presentation — whichever is most convenient for you. 95% of our consultations are done remotely or at your location. No office visit required. We meet you where it's convenient, whether that's your home in West Clay, a coffee shop in the Arts District, or a quick video call during your lunch break.",
      },
      {
        question: "Can you help me if I already have life insurance through my employer (like Salesforce or Allegion)?",
        answer: "Absolutely. Employer-provided life insurance is a great benefit, but it's rarely enough. Most group policies cover only 1–2x your salary (often capped at $50,000–$100,000), and you lose that coverage if you leave your job. We help Carmel professionals supplement their employer coverage with an affordable private policy that stays with you regardless of job changes — and costs far less than most people expect.",
      },
      {
        question: "What carriers do you work with for Carmel residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, Global Atlantic, and more. Being independent means we can shop your application across the entire market to find the best rate and underwriting fit for your health profile and financial goals. You get unbiased advice and the most competitive pricing available in Carmel.",
      },
      {
        question: "Do high-income Carmel families need different life insurance strategies?",
        answer: "Yes — affluent families often benefit from permanent life insurance (whole life, universal life, or indexed universal life) in addition to term coverage. These policies build tax-deferred cash value, can supplement retirement income, and provide estate planning benefits. For Carmel families with incomes above $150,000, we typically recommend a layered strategy: term life for immediate protection plus permanent coverage for long-term wealth building and legacy planning.",
      },
      {
        question: "How does life insurance fit into retirement and wealth planning for Carmel residents?",
        answer: "For high-income Carmel families, life insurance is a key wealth-building tool. Permanent policies offer tax-advantaged growth, can provide tax-free retirement income, and ensure a legacy for your children. We often pair life insurance with annuities to create a comprehensive retirement income strategy. Many Carmel professionals use cash-value life insurance to diversify beyond 401(k)s and IRAs, especially if they've maxed out traditional retirement accounts.",
      },
    ],
  },
  fishers: {
    name: "Fishers",
    population: "98,000+",
    county: "Hamilton County",
    zipCodes: ["46037", "46038", "46040"],
    description: "Fishers is a thriving, family-oriented community consistently ranked among the best places to live in America. With a median household income of $95,000+, excellent schools, and a booming tech sector, Fishers families are building wealth — and they need the right life insurance protection to safeguard it. Whether you live in Saxony, near Geist Reservoir, or in downtown Fishers, we help you protect your family's financial future with affordable, straightforward coverage.",
    neighborhoods: [
      "Saxony",
      "Geist Reservoir Area",
      "Downtown Fishers (The Yard, Nickel Plate District)",
      "Sunblest",
      "Bristols",
      "Lantern Road",
      "Southeastern Fishers (near 116th & Olio)",
      "Southeastern Fishers",
      "Britton Falls",
      "Windermere",
      "Hoosier Woods",
      "Stony Creek",
    ],
    localInsights: [
      "Median household income: $95,000+ (among highest in Indiana)",
      "Homeownership rate: 78% — most families carry mortgage debt",
      "Top employers: Navient, Roche Diagnostics, Ascension St. Vincent, Fishers Town Hall, Launch Fishers (tech hub)",
      "Ranked #1 Best Place to Live in America by Money Magazine (2017)",
      "Average home value: $350,000+ — mortgage protection is essential",
      "Hamilton Southeastern Schools consistently ranked top 5 in Indiana",
      "Fishers has one of the youngest median ages in Indiana (35.2 years) — prime life insurance years",
    ],
    faqs: [
      {
        question: "How much life insurance do Fishers families typically need?",
        answer: "Most Fishers families need $500,000–$1,200,000 in coverage to protect their mortgage (average $300,000+), replace lost income, fund children's education, and maintain their lifestyle. With median household incomes around $95,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount based on your mortgage, debts, dependents, and long-term goals.",
      },
      {
        question: "What's the average cost of life insurance for a Fishers resident?",
        answer: "A healthy 35-year-old in Fishers can secure $500,000 of 20-year term life insurance for approximately $25–$35/month. A 45-year-old in good health might pay $60–$90/month for the same coverage. Costs vary based on age, health, coverage amount, and term length. As an independent agency, we shop your application across dozens of carriers to find the most competitive rate — often saving Fishers families 20–30% compared to buying direct.",
      },
      {
        question: "Do you meet Fishers residents in person, or is everything done remotely?",
        answer: "We offer complete flexibility: phone consultations, video conferences (Zoom/Google Meet), or in-home presentations at your Fishers residence. 95% of our consultations are done remotely or at your location — no office visit required. Whether you're in Saxony, near Geist, or downtown Fishers, we meet you where it's convenient. Many Fishers families prefer a quick video call during lunch or after the kids are in bed.",
      },
      {
        question: "Can you help me supplement my employer life insurance from Navient or Roche?",
        answer: "Yes — and we strongly recommend it. Employer-provided life insurance is a valuable benefit, but it's typically limited to 1–2x your salary (often capped at $50,000–$100,000) and disappears if you change jobs. We help Fishers professionals add affordable private coverage that stays with you for life, regardless of employment changes. Supplemental coverage is often much less expensive than people expect — and it's the only way to ensure your family is fully protected.",
      },
      {
        question: "What insurance carriers do you work with for Fishers residents?",
        answer: "We're an independent agency representing dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, Global Atlantic, and many more. This allows us to shop the entire market on your behalf and find the best rate and underwriting fit for your unique health profile and financial situation. You get unbiased advice and the most competitive pricing available in Fishers.",
      },
      {
        question: "I'm a young family in Fishers with a new mortgage — what type of life insurance should I get?",
        answer: "For young Fishers families (ages 25–40) with a new mortgage and young children, 20-year or 30-year term life insurance is usually the best fit. It provides maximum coverage at the lowest cost during your highest-risk years (when you have young kids, a big mortgage, and limited savings). A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old. Once your mortgage is paid down and kids are grown, you can reassess your needs.",
      },
      {
        question: "How does life insurance work with my 529 college savings plan for my kids?",
        answer: "Life insurance and 529 plans work together to protect your children's education. Your 529 covers college costs if everything goes as planned. Life insurance ensures those funds are still available (and then some) if something happens to you. Many Fishers parents get enough coverage to replace 10–15 years of income, which would fully fund college expenses plus living costs for their spouse and kids. It's the ultimate safety net for your family's financial plan.",
      },
    ],
  },
  indianapolis: {
    name: "Indianapolis",
    population: "887,000+",
    county: "Marion County",
    zipCodes: ["46201", "46202", "46203", "46204", "46205", "46208", "46214", "46218", "46220", "46222", "46224", "46226", "46228", "46240", "46250", "46254", "46256", "46260", "46268"],
    description: "Indianapolis is Indiana's capital and largest city, home to nearly 900,000 residents and a thriving economy anchored by healthcare, finance, manufacturing, and sports. From Broad Ripple to Southport, Castleton to Speedway, Indy families face real financial risks — and life insurance is the foundation of any solid financial plan. Whether you're a young professional downtown, a growing family on the north side, or a retiree on the east side, we help Indianapolis residents find affordable, reliable coverage.",
    neighborhoods: [
      "Broad Ripple",
      "Meridian-Kessler",
      "Irvington",
      "Fountain Square",
      "Castleton",
      "Lawrence",
      "Speedway",
      "Beech Grove",
      "Pike Township",
      "Warren Township",
      "Washington Township",
      "Perry Township",
    ],
    localInsights: [
      "Largest city in Indiana with nearly 900,000 residents",
      "Major employers: IU Health, Eli Lilly, Salesforce, Anthem, Rolls-Royce, Cummins",
      "Home to the Indianapolis Motor Speedway and multiple professional sports teams",
      "Median household income: $52,000 — life insurance is affordable and essential",
      "Homeownership rate: 55% — mortgage protection is a top priority",
      "Marion County has one of the highest uninsured rates in Indiana — we help close that gap",
      "Diverse neighborhoods from downtown condos to suburban family homes",
    ],
    faqs: [
      {
        question: "How much does life insurance cost for an Indianapolis resident?",
        answer: "A healthy 35-year-old in Indianapolis can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, coverage amount, and term length. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation — often saving Indianapolis families 20–30% compared to going direct to a single insurer.",
      },
      {
        question: "What types of life insurance do Indianapolis families need most?",
        answer: "Most Indianapolis families benefit most from term life insurance — it provides the highest coverage at the lowest cost during your peak earning and family-raising years. Mortgage protection is also popular for homeowners. Seniors often choose final expense insurance. High-income professionals may benefit from permanent policies like whole life or indexed universal life for tax-advantaged wealth building.",
      },
      {
        question: "Do you serve all Indianapolis neighborhoods and ZIP codes?",
        answer: "Yes — we serve all of Indianapolis and Marion County, including Broad Ripple, Meridian-Kessler, Irvington, Fountain Square, Castleton, Lawrence, Speedway, Beech Grove, Pike Township, Warren Township, Washington Township, and Perry Township. We meet clients by phone, video, or in-home appointment — wherever is most convenient for you.",
      },
      {
        question: "Can I get life insurance if I work for Eli Lilly or IU Health?",
        answer: "Absolutely. Employer-provided life insurance is a great start, but it's rarely enough — typically only 1–2x your salary — and you lose it if you change jobs. We help Indianapolis professionals supplement their employer coverage with affordable private policies that stay with them for life. Many Eli Lilly and IU Health employees are surprised how inexpensive supplemental coverage can be.",
      },
      {
        question: "What if I have health issues — can I still get life insurance in Indianapolis?",
        answer: "Yes. We specialize in helping Indianapolis residents with health conditions find coverage. We work with dozens of carriers, each with different underwriting guidelines. Conditions like diabetes, high blood pressure, sleep apnea, and even some heart conditions don't automatically disqualify you. We know which carriers are most lenient for specific conditions and can often find coverage when others can't.",
      },
    ],
  },
  westfield: {
    name: "Westfield",
    population: "48,000+",
    county: "Hamilton County",
    zipCodes: ["46074"],
    description: "Westfield is one of Indiana's fastest-growing cities, with a booming population, top-rated schools, and a strong sense of community. Home to Grand Park Sports Campus — the largest youth sports complex in the nation — Westfield attracts young, active families who are building their lives and need solid financial protection. With rising home values and a growing population, Westfield families need solid life insurance protection to safeguard their financial future.",
    neighborhoods: [
      "Grand Park Area",
      "Chatham Hills",
      "Carey Road Corridor",
      "Spring Mill Crossing",
      "Westfield Village",
      "Oak Manor",
      "Harmony",
      "Bridgewater Club",
      "Centennial",
      "Grassy Branch",
    ],
    localInsights: [
      "One of Indiana's fastest-growing cities — population up 40% in the last decade",
      "Home to Grand Park Sports Campus, the largest youth sports complex in the US",
      "Median household income: $95,000+ (Hamilton County)",
      "Homeownership rate: 80%+ — mortgage protection is critical",
      "Westfield Washington Schools rated among top districts in Indiana",
      "Average home value: $380,000+ and rising rapidly",
      "Strong young family demographic — prime life insurance years",
    ],
    faqs: [
      {
        question: "How much life insurance do Westfield families typically need?",
        answer: "Most Westfield families need $500,000–$1,200,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $95,000 and rising home values, a good rule of thumb is 10–12x your annual income. We provide a free Financial Needs Analysis to calculate your exact number.",
      },
      {
        question: "What's the best life insurance for a young Westfield family?",
        answer: "For young Westfield families (ages 25–40) with a new mortgage and young children, 20-year or 30-year term life insurance is usually the best fit. It provides maximum coverage at the lowest cost during your highest-risk years. A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old.",
      },
      {
        question: "Do you serve all Westfield neighborhoods?",
        answer: "Yes — we serve all Westfield neighborhoods including Grand Park area, Chatham Hills, Carey Road Corridor, Harmony, Bridgewater Club, and all surrounding communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance quickly in Westfield?",
        answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in as little as 24–48 hours. Traditional policies with a medical exam typically take 2–4 weeks. We'll recommend the fastest path based on your health profile and coverage needs.",
      },
      {
        question: "What insurance carriers do you work with for Westfield residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, Global Atlantic, and more. Being independent means we shop the entire market to find the best rate and underwriting fit for your unique situation.",
      },
    ],
  },
  noblesville: {
    name: "Noblesville",
    population: "75,000+",
    county: "Hamilton County",
    zipCodes: ["46060", "46062"],
    description: "Noblesville is a charming, historic city in Hamilton County that has grown into one of Indiana's most desirable communities. With a vibrant downtown square, excellent schools, and a strong local economy, Noblesville families are building real wealth — and they need the right life insurance protection to safeguard it. From young families near Morse Reservoir to established professionals in Hazel Dell, we help Noblesville residents find affordable, comprehensive coverage.",
    neighborhoods: [
      "Downtown Noblesville Square",
      "Morse Reservoir Area",
      "Hazel Dell",
      "Prairie Lakes",
      "Sagamore",
      "Stony Creek",
      "Hinkle Creek",
      "Harbour Trees",
      "Lantern Road Corridor",
      "Southeastern Noblesville",
    ],
    localInsights: [
      "Historic downtown square with thriving local businesses and restaurants",
      "Morse Reservoir — one of Indiana's most popular recreational lakes",
      "Median household income: $85,000+ (Hamilton County)",
      "Homeownership rate: 75%+ — mortgage protection is a top priority",
      "Noblesville Schools consistently ranked top 10 in Indiana",
      "Average home value: $320,000+ and growing",
      "Strong mix of young families and established professionals",
    ],
    faqs: [
      {
        question: "How much life insurance do Noblesville families need?",
        answer: "Most Noblesville families need $400,000–$1,000,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $85,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Noblesville?",
        answer: "A healthy 35-year-old in Noblesville can get $500,000 of 20-year term life insurance for $25–$35/month. A 45-year-old in good health might pay $60–$90/month for the same coverage. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific profile.",
      },
      {
        question: "Do you serve all Noblesville neighborhoods?",
        answer: "Yes — we serve all Noblesville neighborhoods including downtown, Morse Reservoir area, Hazel Dell, Prairie Lakes, Sagamore, and all surrounding communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance if I have a pre-existing condition?",
        answer: "Yes. We work with dozens of carriers, each with different underwriting guidelines. Many common conditions like high blood pressure, diabetes, and sleep apnea don't automatically disqualify you. We know which carriers are most lenient for specific conditions and can often find coverage when others can't.",
      },
      {
        question: "What types of life insurance are most popular in Noblesville?",
        answer: "Term life insurance is the most popular choice for Noblesville families — it provides maximum coverage at the lowest cost. Mortgage protection is also very popular given the high homeownership rate. Seniors often choose final expense insurance. High-income professionals may benefit from permanent policies for tax-advantaged wealth building.",
      },
    ],
  },
  brownsburg: {
    name: "Brownsburg",
    population: "28,000+",
    county: "Hendricks County",
    zipCodes: ["46112"],
    description: "Brownsburg is a thriving suburban community in Hendricks County, known for its excellent schools, family-friendly neighborhoods, and proximity to Indianapolis. Home to the famous Lucas Oil Raceway, Brownsburg has a strong community identity and a growing population of young families and professionals. With rising home values and a strong local economy, Brownsburg residents need solid life insurance protection to safeguard their financial future.",
    neighborhoods: [
      "Downtown Brownsburg",
      "Lincoln Park",
      "Arbor Grove",
      "Brownsburg Meadows",
      "Wyndemere",
      "Northfield",
      "Quail Run",
      "Stonegate",
      "Raceway Area",
      "Eagle Pines",
    ],
    localInsights: [
      "Home to Lucas Oil Raceway — a major motorsports destination",
      "Median household income: $78,000+ (Hendricks County)",
      "Homeownership rate: 78% — mortgage protection is essential",
      "Brownsburg Community School Corporation rated among top districts in Indiana",
      "Average home value: $290,000+ and growing",
      "Strong young family demographic — prime life insurance years",
      "Easy access to Indianapolis via I-74 and US-136",
    ],
    faqs: [
      {
        question: "How much life insurance do Brownsburg families need?",
        answer: "Most Brownsburg families need $400,000–$900,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $78,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the best life insurance for a Brownsburg family?",
        answer: "For most Brownsburg families, 20-year or 30-year term life insurance is the best fit — it provides maximum coverage at the lowest cost during your peak earning and family-raising years. A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old.",
      },
      {
        question: "Do you serve all Brownsburg neighborhoods?",
        answer: "Yes — we serve all Brownsburg neighborhoods and surrounding Hendricks County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance quickly in Brownsburg?",
        answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in 24–48 hours. We'll recommend the fastest path based on your health profile and coverage needs.",
      },
      {
        question: "What insurance carriers do you work with for Brownsburg residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  zionsville: {
    name: "Zionsville",
    population: "30,000+",
    county: "Boone County",
    zipCodes: ["46077"],
    description: "Zionsville is one of Indiana's most charming and affluent communities, known for its picturesque brick-paved Main Street, top-rated schools, and high quality of life. With median household incomes among the highest in the state and a strong culture of financial planning, Zionsville families understand the importance of protecting their wealth. Whether you live near the Village, in Eagle Creek, or in one of Zionsville's beautiful new developments, we help you find the right life insurance strategy.",
    neighborhoods: [
      "The Village (Main Street)",
      "Eagle Creek",
      "Zionsville Community Schools Area",
      "Stonegate",
      "Holliday Farms",
      "Traders Point",
      "Whitestown Road Corridor",
      "Boone Village",
      "Creekside",
      "Quail Run",
    ],
    localInsights: [
      "Median household income: $110,000+ (among highest in Indiana)",
      "Homeownership rate: 85%+ — most families carry significant mortgage debt",
      "Zionsville Community Schools consistently ranked top 5 in Indiana",
      "Average home value: $480,000+ — mortgage protection is critical",
      "Charming brick-paved Main Street with thriving local businesses",
      "Strong culture of financial planning and wealth management",
      "Boone County has one of the lowest crime rates in Indiana",
    ],
    faqs: [
      {
        question: "How much life insurance do Zionsville families typically need?",
        answer: "Most Zionsville families need $600,000–$1,500,000 in coverage to fully protect their mortgage (average $400,000+), replace lost income, and fund their children's education. With median household incomes above $110,000, a good rule of thumb is 10–12x your annual income. We provide a free Financial Needs Analysis to calculate your exact number.",
      },
      {
        question: "Do high-income Zionsville families need different life insurance strategies?",
        answer: "Yes — affluent families often benefit from permanent life insurance (whole life, universal life, or indexed universal life) in addition to term coverage. These policies build tax-deferred cash value, can supplement retirement income, and provide estate planning benefits. We typically recommend a layered strategy: term life for immediate protection plus permanent coverage for long-term wealth building.",
      },
      {
        question: "What's the average cost of life insurance for a Zionsville resident?",
        answer: "A healthy 35-year-old in Zionsville can get $500,000 of 20-year term life insurance for $25–$35/month. A 45-year-old might pay $60–$90/month for the same coverage. As an independent agency, we shop dozens of carriers to find the most competitive rate — often saving Zionsville families 20–30% compared to going direct.",
      },
      {
        question: "Do you serve all Zionsville neighborhoods?",
        answer: "Yes — we serve all Zionsville neighborhoods including the Village, Eagle Creek, Holliday Farms, Traders Point, and all surrounding Boone County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What insurance carriers do you work with for Zionsville residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, Global Atlantic, and more. Being independent means we shop the entire market to find the best rate and underwriting fit for your unique situation.",
      },
    ],
  },
  whitestown: {
    name: "Whitestown",
    population: "12,000+",
    county: "Boone County",
    zipCodes: ["46075"],
    description: "Whitestown is one of Indiana's fastest-growing towns, with explosive population growth driven by new housing developments, excellent schools, and easy access to both Indianapolis and Zionsville. Young families are flocking to Whitestown for its affordable homes, low crime rates, and strong community feel. As Whitestown continues to revitalize and attract new residents, life insurance is an important part of financial planning for its families.",
    neighborhoods: [
      "Anson",
      "Harvest Meadows",
      "Whitestown Town Center",
      "Stonegate",
      "Lakewood",
      "Northfield",
      "Wyndemere",
      "Creekside",
    ],
    localInsights: [
      "One of the fastest-growing towns in Indiana — population up 300%+ in 10 years",
      "Median household income: $85,000+ (Boone County)",
      "Homeownership rate: 82% — most families carry new mortgage debt",
      "Average home value: $310,000+ and rising rapidly",
      "Strong young family demographic — prime life insurance years",
      "Zionsville Community Schools serve Whitestown residents",
      "Easy access to I-65 and US-52 for Indianapolis commuters",
    ],
    faqs: [
      {
        question: "How much life insurance do Whitestown families need?",
        answer: "Most Whitestown families need $400,000–$900,000 in coverage to protect their new mortgage, replace lost income, and fund their children's education. With median household incomes around $85,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the best life insurance for a new Whitestown homeowner?",
        answer: "For new Whitestown homeowners, 20-year or 30-year term life insurance is usually the best fit — it provides maximum coverage at the lowest cost during your highest-risk years. A $400,000 policy costs as little as $20–$30/month for a healthy 30-year-old. Mortgage protection insurance is also a popular option.",
      },
      {
        question: "Do you serve all Whitestown neighborhoods?",
        answer: "Yes — we serve all Whitestown neighborhoods and surrounding Boone County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance quickly in Whitestown?",
        answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in 24–48 hours. We'll recommend the fastest path based on your health profile and coverage needs.",
      },
      {
        question: "What insurance carriers do you work with for Whitestown residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  lebanon: {
    name: "Lebanon",
    population: "16,000+",
    county: "Boone County",
    zipCodes: ["46052"],
    description: "Lebanon is the county seat of Boone County and a growing community that blends small-town charm with modern amenities. Located along I-65, Lebanon is home to a mix of long-established families and new residents drawn by affordable housing and a strong local economy. As Lebanon continues to grow, more families are recognizing the importance of life insurance to protect their homes, income, and loved ones.",
    neighborhoods: [
      "Downtown Lebanon",
      "Lebanon Square",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Rural Boone County",
    ],
    localInsights: [
      "County seat of Boone County with a growing population",
      "Median household income: $65,000+ (Boone County)",
      "Homeownership rate: 70% — mortgage protection is important",
      "Average home value: $220,000+ and growing",
      "Located along I-65 — easy access to Indianapolis and Lafayette",
      "Strong agricultural and manufacturing economy",
      "Lebanon Community School Corporation serves local families",
    ],
    faqs: [
      {
        question: "How much life insurance do Lebanon families need?",
        answer: "Most Lebanon families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $65,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Lebanon, Indiana?",
        answer: "A healthy 35-year-old in Lebanon can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Lebanon neighborhoods?",
        answer: "Yes — we serve all Lebanon neighborhoods and surrounding Boone County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance if I'm a farmer or self-employed in Lebanon?",
        answer: "Absolutely. We work with many self-employed individuals and farmers in Boone County. Self-employed people often have no employer-provided life insurance, making private coverage even more important. We can help you find affordable coverage that fits your income and budget.",
      },
      {
        question: "What types of life insurance are most popular in Lebanon?",
        answer: "Term life insurance is the most popular choice for Lebanon families — it provides maximum coverage at the lowest cost. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  mooresville: {
    name: "Mooresville",
    population: "12,000+",
    county: "Morgan County",
    zipCodes: ["47158"],
    description: "Mooresville is a growing community in Morgan County, located just southwest of Indianapolis. Known for its friendly small-town atmosphere and affordable housing, Mooresville attracts families looking for a quieter lifestyle within easy commuting distance of Indianapolis. As the community grows and home values rise, life insurance becomes an increasingly important part of financial planning for Mooresville families.",
    neighborhoods: [
      "Downtown Mooresville",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Rural Morgan County",
      "Mooresville Crossing",
    ],
    localInsights: [
      "Growing community in Morgan County with affordable housing",
      "Median household income: $62,000+ (Morgan County)",
      "Homeownership rate: 72% — mortgage protection is important",
      "Average home value: $210,000+ and growing",
      "Easy access to SR-67 and I-70",
      "Strong community feel with local schools and businesses",
      "Morgan County has a strong agricultural heritage",
    ],
    faqs: [
      {
        question: "How much life insurance do Mooresville families need?",
        answer: "Most Mooresville families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $62,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Mooresville?",
        answer: "A healthy 35-year-old in Mooresville can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve Mooresville and surrounding Morgan County communities?",
        answer: "Yes — we serve Mooresville and all of Morgan County, including Martinsville and surrounding rural communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance if I have health issues?",
        answer: "Yes. We work with dozens of carriers, each with different underwriting guidelines. Many common conditions don't automatically disqualify you. We know which carriers are most lenient for specific conditions and can often find coverage when others can't.",
      },
      {
        question: "What types of life insurance are most popular in Mooresville?",
        answer: "Term life insurance is the most popular choice for Mooresville families. Final expense insurance is very popular among Mooresville seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  castleton: {
    name: "Castleton",
    population: "25,000+",
    county: "Marion County",
    zipCodes: ["46250", "46256"],
    description: "Castleton is a vibrant northeast Indianapolis community known for its major retail corridor, excellent dining, and convenient location near I-465 and I-69. A mix of established families, young professionals, and retirees call Castleton home. With easy access to Fishers, Geist, and downtown Indianapolis, Castleton residents enjoy the best of suburban living with urban convenience — and they need life insurance protection to match their busy, productive lives.",
    neighborhoods: [
      "Castleton Square Area",
      "Allisonville Road Corridor",
      "82nd Street Corridor",
      "Geist Reservoir Nearby",
      "Oaklandon",
      "Sunnyside",
      "Castleton Farms",
      "Windermere",
    ],
    localInsights: [
      "Major retail and commercial hub in northeast Indianapolis",
      "Median household income: $68,000+ (northeast Marion County)",
      "Homeownership rate: 65% — mortgage protection is important",
      "Average home value: $260,000+ and growing",
      "Easy access to I-465 and I-69 — popular with commuters",
      "Close proximity to Fishers and Geist Reservoir",
      "Strong mix of families, professionals, and retirees",
    ],
    faqs: [
      {
        question: "How much life insurance do Castleton residents need?",
        answer: "Most Castleton residents need $350,000–$800,000 in coverage to protect their mortgage, replace lost income, and provide for their families. With median household incomes around $68,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Castleton?",
        answer: "A healthy 35-year-old in Castleton can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Castleton neighborhoods?",
        answer: "Yes — we serve all Castleton neighborhoods and surrounding northeast Indianapolis communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance quickly in Castleton?",
        answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in 24–48 hours. We'll recommend the fastest path based on your health profile and coverage needs.",
      },
      {
        question: "What types of life insurance are most popular in Castleton?",
        answer: "Term life insurance is the most popular choice for Castleton families. Mortgage protection is important for homeowners. Final expense insurance is popular among seniors. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  geist: {
    name: "Geist",
    population: "15,000+",
    county: "Hamilton/Marion County",
    zipCodes: ["46038", "46256"],
    description: "Geist is one of Indianapolis's most desirable lakeside communities, centered around the beautiful Geist Reservoir. Known for its upscale homes, boating lifestyle, and proximity to Fishers and Castleton, Geist attracts high-income professionals and families who have built significant wealth. With luxury homes, active lifestyles, and complex financial situations, Geist residents need sophisticated life insurance strategies to protect everything they've worked for.",
    neighborhoods: [
      "Geist Reservoir Waterfront",
      "Admirals Pointe",
      "Geist Cove",
      "Windermere",
      "Oaklandon",
      "Sunnyside",
      "Geist Estates",
      "Harbour Lights",
    ],
    localInsights: [
      "Upscale lakeside community centered around Geist Reservoir",
      "Median household income: $105,000+ (one of the highest in the Indianapolis area)",
      "Homeownership rate: 88% — most families carry significant mortgage debt",
      "Average home value: $520,000+ — mortgage protection is critical",
      "Popular boating and recreational community",
      "Close proximity to Fishers and Castleton retail/dining",
      "Strong mix of high-income professionals and established families",
    ],
    faqs: [
      {
        question: "How much life insurance do Geist families typically need?",
        answer: "Most Geist families need $600,000–$1,500,000 in coverage to fully protect their mortgage (average $450,000+), replace lost income, and fund their children's education. With median household incomes above $105,000, a good rule of thumb is 10–12x your annual income. We provide a free Financial Needs Analysis to calculate your exact number.",
      },
      {
        question: "Do high-income Geist families need different life insurance strategies?",
        answer: "Yes — affluent families often benefit from permanent life insurance (whole life, universal life, or indexed universal life) in addition to term coverage. These policies build tax-deferred cash value, can supplement retirement income, and provide estate planning benefits. We typically recommend a layered strategy: term life for immediate protection plus permanent coverage for long-term wealth building.",
      },
      {
        question: "What's the average cost of life insurance for a Geist resident?",
        answer: "A healthy 35-year-old in Geist can get $500,000 of 20-year term life insurance for $25–$35/month. A 45-year-old might pay $60–$90/month for the same coverage. As an independent agency, we shop dozens of carriers to find the most competitive rate — often saving Geist families 20–30% compared to going direct.",
      },
      {
        question: "Do you serve all Geist neighborhoods?",
        answer: "Yes — we serve all Geist neighborhoods including Admirals Pointe, Geist Cove, Windermere, and all surrounding communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What insurance carriers do you work with for Geist residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, Global Atlantic, and more. Being independent means we can shop the entire market to find the best rate and underwriting fit for your unique health profile and financial situation. You get unbiased advice and the most competitive pricing available in Geist.",
      },
    ],
  },
  "broad-ripple": {
    name: "Broad Ripple",
    population: "14,000+",
    county: "Marion County",
    zipCodes: ["46220", "46208"],
    description: "Broad Ripple is one of Indianapolis's most vibrant and eclectic neighborhoods, known for its arts scene, restaurants, nightlife, and the beautiful White River Greenway. Home to a mix of young professionals, artists, families, and long-time residents, Broad Ripple has a unique character that sets it apart from other Indianapolis neighborhoods. As property values rise and residents build their careers and families, life insurance becomes an essential part of financial planning.",
    neighborhoods: [
      "Broad Ripple Village",
      "Meridian-Kessler",
      "Warfleigh",
      "Nora",
      "North Meridian",
      "Ravenswood",
      "Brendonwood",
      "Allisonville",
    ],
    localInsights: [
      "One of Indianapolis's most vibrant and eclectic neighborhoods",
      "Median household income: $58,000+ (north-central Marion County)",
      "Homeownership rate: 55% — mix of owners and renters",
      "Average home value: $280,000+ and rising rapidly",
      "Strong arts, dining, and entertainment scene",
      "Popular with young professionals and creative class",
      "White River Greenway and Monon Trail access",
    ],
    faqs: [
      {
        question: "How much life insurance do Broad Ripple residents need?",
        answer: "Most Broad Ripple residents need $300,000–$800,000 in coverage to protect their mortgage or provide income replacement for their families. With median household incomes around $58,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the best life insurance for a young professional in Broad Ripple?",
        answer: "For young professionals in Broad Ripple (ages 25–35), term life insurance is usually the best fit — it provides maximum coverage at the lowest cost. A $500,000 policy costs as little as $20–$30/month for a healthy 28-year-old. Locking in rates while you're young and healthy is the smartest financial move you can make.",
      },
      {
        question: "Do you serve all Broad Ripple neighborhoods?",
        answer: "Yes — we serve all Broad Ripple neighborhoods including Broad Ripple Village, Meridian-Kessler, Warfleigh, Nora, and all surrounding communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can renters in Broad Ripple benefit from life insurance?",
        answer: "Absolutely. Life insurance isn't just for homeowners. If you have dependents, student loans, or anyone who relies on your income, life insurance is essential. Even if you're single, locking in low rates now means you'll have affordable coverage when you do have a family or mortgage.",
      },
      {
        question: "What insurance carriers do you work with for Broad Ripple residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  greenwood: {
    name: "Greenwood",
    population: "65,000+",
    county: "Johnson County",
    zipCodes: ["46142", "46143"],
    description: "Greenwood is one of Indianapolis's fastest-growing suburbs, located just south of the city in Johnson County. Known for its excellent schools, family-friendly neighborhoods, and the popular Greenwood Park Mall area, Greenwood attracts young families and professionals looking for affordable suburban living with easy access to Indianapolis. With rising home values and a growing population, Greenwood families need solid life insurance protection to safeguard their financial future.",
    neighborhoods: [
      "Greenwood Park Mall Area",
      "Old Town Greenwood",
      "Stones Crossing",
      "Southwood",
      "Westwood",
      "Northwood",
      "Eastwood",
      "Morgantown Road Corridor",
      "Smith Valley Road Area",
      "Worthsville Road Corridor",
    ],
    localInsights: [
      "One of Indianapolis's fastest-growing suburbs",
      "Median household income: $72,000+ (Johnson County)",
      "Homeownership rate: 72% — mortgage protection is important",
      "Average home value: $270,000+ and growing rapidly",
      "Center Grove Community School Corporation rated top 10 in Indiana",
      "Easy access to Indianapolis via I-65 and US-31",
      "Strong mix of young families and established professionals",
    ],
    faqs: [
      {
        question: "How much life insurance do Greenwood families need?",
        answer: "Most Greenwood families need $400,000–$900,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $72,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Greenwood?",
        answer: "A healthy 35-year-old in Greenwood can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Greenwood neighborhoods?",
        answer: "Yes — we serve all Greenwood neighborhoods and surrounding Johnson County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What's the best life insurance for a young Greenwood family with young children?",
        answer: "For Greenwood families with young children, 20-year or 30-year term life insurance is usually the best fit — it provides maximum coverage at the lowest cost during your highest-risk years. A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old.",
      },
      {
        question: "What insurance carriers do you work with for Greenwood residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  avon: {
    name: "Avon",
    population: "20,000+",
    county: "Hendricks County",
    zipCodes: ["46123"],
    description: "Avon is a thriving suburban community in Hendricks County, known for its excellent schools, family-friendly neighborhoods, and rapid growth. Located just west of Indianapolis along US-36, Avon has become one of the most desirable communities in the Indianapolis metro area. With rising home values and a strong young family demographic, Avon residents need solid life insurance protection to safeguard their financial future.",
    neighborhoods: [
      "Avon Town Center",
      "Prestwick",
      "Bridgewater",
      "Heartland Crossing",
      "Avon Commons",
      "Northfield",
      "Wyndemere",
      "Eagle Pines",
      "Stonegate",
      "Quail Run",
    ],
    localInsights: [
      "One of Hendricks County's fastest-growing communities",
      "Median household income: $82,000+ (Hendricks County)",
      "Homeownership rate: 80% — mortgage protection is essential",
      "Average home value: $300,000+ and growing",
      "Avon Community School Corporation rated among top districts in Indiana",
      "Easy access to Indianapolis via US-36 and I-465",
      "Strong young family demographic — prime life insurance years",
    ],
    faqs: [
      {
        question: "How much life insurance do Avon families need?",
        answer: "Most Avon families need $400,000–$900,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $82,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the best life insurance for an Avon family?",
        answer: "For most Avon families, 20-year or 30-year term life insurance is the best fit — it provides maximum coverage at the lowest cost during your peak earning and family-raising years. A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old.",
      },
      {
        question: "Do you serve all Avon neighborhoods?",
        answer: "Yes — we serve all Avon neighborhoods and surrounding Hendricks County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance quickly in Avon?",
        answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in 24–48 hours. We'll recommend the fastest path based on your health profile and coverage needs.",
      },
      {
        question: "What insurance carriers do you work with for Avon residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  plainfield: {
    name: "Plainfield",
    population: "35,000+",
    county: "Hendricks County",
    zipCodes: ["46168"],
    description: "Plainfield is a growing community in Hendricks County, strategically located near Indianapolis International Airport and major interstate highways. Known for its affordable housing, excellent schools, and strong logistics and distribution economy, Plainfield attracts a diverse mix of families and professionals. As the community continues to grow, life insurance is an essential part of financial planning for Plainfield families.",
    neighborhoods: [
      "Downtown Plainfield",
      "Plainfield Town Center",
      "Northfield",
      "Southfield",
      "Eastfield",
      "Westfield",
      "Airport Corridor",
      "Quaker Boulevard Area",
    ],
    localInsights: [
      "Strategic location near Indianapolis International Airport",
      "Median household income: $72,000+ (Hendricks County)",
      "Homeownership rate: 72% — mortgage protection is important",
      "Average home value: $250,000+ and growing",
      "Strong logistics and distribution economy (FedEx, Amazon, UPS hubs)",
      "Easy access to I-70 and I-465",
      "Plainfield Community School Corporation serves local families",
    ],
    faqs: [
      {
        question: "How much life insurance do Plainfield families need?",
        answer: "Most Plainfield families need $350,000–$800,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $72,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Plainfield?",
        answer: "A healthy 35-year-old in Plainfield can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Plainfield neighborhoods?",
        answer: "Yes — we serve all Plainfield neighborhoods and surrounding Hendricks County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What's the best life insurance for a Plainfield family?",
        answer: "For most Plainfield families, 20-year or 30-year term life insurance is the best fit — it provides maximum coverage at the lowest cost during your peak earning and family-raising years. A $500,000 policy costs as little as $25–$35/month for a healthy 35-year-old.",
      },
      {
        question: "Can I get life insurance if I work in logistics or distribution?",
        answer: "Absolutely. We work with many logistics and distribution workers in Plainfield. Your occupation may affect your rates slightly, but most logistics workers qualify for standard or preferred rates. We'll find the best carrier for your specific occupation and health profile.",
      },
      {
        question: "What insurance carriers do you work with for Plainfield residents?",
        answer: "We represent dozens of top-rated carriers including Mutual of Omaha, Transamerica, Foresters Financial, North American Company, Americo, and more. Being independent means we shop the entire market to find the best rate for your unique situation.",
      },
    ],
  },
  lawrence: {
    name: "Lawrence",
    population: "50,000+",
    county: "Marion County",
    zipCodes: ["46226", "46235", "46236"],
    description: "Lawrence is a growing city on the northeast side of Indianapolis, known for its diverse community, affordable housing, and strong military heritage as home to Fort Benjamin Harrison. Lawrence offers a mix of established neighborhoods and new developments, attracting families looking for affordable suburban living within Marion County. As Lawrence continues to grow and diversify, life insurance is an essential part of financial planning for its residents.",
    neighborhoods: [
      "Fort Harrison State Park Area",
      "Oaklandon",
      "Sunnyside",
      "Castleton Nearby",
      "Geist Nearby",
      "Lawrence Town Center",
      "Pendleton Pike Corridor",
      "56th Street Corridor",
    ],
    localInsights: [
      "Home to Fort Harrison State Park — a popular recreational destination",
      "Median household income: $55,000+ (northeast Marion County)",
      "Homeownership rate: 62% — mortgage protection is important",
      "Average home value: $220,000+ and growing",
      "Diverse community with strong military heritage",
      "Easy access to I-465 and I-69",
      "MSD of Lawrence Township serves local families",
    ],
    faqs: [
      {
        question: "How much life insurance do Lawrence residents need?",
        answer: "Most Lawrence residents need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $55,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Lawrence, Indiana?",
        answer: "A healthy 35-year-old in Lawrence can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Lawrence neighborhoods?",
        answer: "Yes — we serve all Lawrence neighborhoods and surrounding northeast Indianapolis communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can veterans and military families in Lawrence get life insurance?",
        answer: "Absolutely. We work with many veterans and military families in the Lawrence area. Veterans may qualify for excellent rates, especially if they're in good health. We can help you supplement your VA or SGLI coverage with affordable private policies that provide additional protection for your family.",
      },
      {
        question: "What types of life insurance are most popular in Lawrence?",
        answer: "Term life insurance is the most popular choice for Lawrence families. Mortgage protection is important for homeowners. Final expense insurance is popular among seniors. High-income professionals may benefit from permanent policies for tax-advantaged wealth building.",
      },
    ],
  },
  "beech-grove": {
    name: "Beech Grove",
    population: "15,000+",
    county: "Marion County",
    zipCodes: ["46107"],
    description: "Beech Grove is a small, tight-knit city completely surrounded by Indianapolis, known for its strong community spirit, affordable housing, and proud working-class heritage. Home to the historic Amtrak maintenance facility and a diverse mix of long-time residents and new families, Beech Grove offers affordable homeownership within easy reach of all Indianapolis has to offer. Life insurance is an essential part of financial planning for Beech Grove families.",
    neighborhoods: [
      "Downtown Beech Grove",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Amtrak Area",
      "Main Street Corridor",
    ],
    localInsights: [
      "Small city completely surrounded by Indianapolis",
      "Median household income: $52,000+ (Marion County)",
      "Homeownership rate: 65% — mortgage protection is important",
      "Average home value: $185,000+ and growing",
      "Strong working-class community with proud local identity",
      "Home to historic Amtrak maintenance facility",
      "Beech Grove City Schools serves local families",
    ],
    faqs: [
      {
        question: "How much life insurance do Beech Grove families need?",
        answer: "Most Beech Grove families need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $52,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Beech Grove?",
        answer: "A healthy 35-year-old in Beech Grove can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Beech Grove neighborhoods?",
        answer: "Yes — we serve all Beech Grove neighborhoods and surrounding Indianapolis communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get affordable life insurance on a working-class budget?",
        answer: "Absolutely. Life insurance is more affordable than most people think. A $250,000 policy can cost as little as $15–$20/month for a healthy 35-year-old. We work with many working-class families in Beech Grove and always find options that fit their budget.",
      },
      {
        question: "What types of life insurance are most popular in Beech Grove?",
        answer: "Term life insurance is the most popular choice for Beech Grove families — it provides maximum coverage at the lowest cost. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  speedway: {
    name: "Speedway",
    population: "12,000+",
    county: "Marion County",
    zipCodes: ["46224"],
    description: "Speedway is a small town completely surrounded by Indianapolis, world-famous as the home of the Indianapolis Motor Speedway and the Indy 500. Beyond the racing heritage, Speedway is a close-knit community with affordable housing, strong neighborhood pride, and a growing arts and dining scene. As Speedway continues to revitalize and attract new residents, life insurance is an important part of financial planning for its families.",
    neighborhoods: [
      "Indianapolis Motor Speedway Area",
      "Downtown Speedway",
      "Northside",
      "Southside",
      "Main Street Corridor",
      "16th Street Corridor",
    ],
    localInsights: [
      "Home of the Indianapolis Motor Speedway and the Indy 500",
      "Median household income: $50,000+ (Marion County)",
      "Homeownership rate: 60% — mortgage protection is important",
      "Average home value: $175,000+ and growing",
      "Growing arts and dining scene along Main Street",
      "Strong community identity centered around racing heritage",
      "Easy access to I-465 and downtown Indianapolis",
    ],
    faqs: [
      {
        question: "How much life insurance do Speedway residents need?",
        answer: "Most Speedway residents need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $50,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Speedway, Indiana?",
        answer: "A healthy 35-year-old in Speedway can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Speedway neighborhoods?",
        answer: "Yes — we serve all Speedway neighborhoods and surrounding Indianapolis communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get affordable life insurance in Speedway?",
        answer: "Absolutely. Life insurance is more affordable than most people think. A $250,000 policy can cost as little as $15–$20/month for a healthy 35-year-old. We work with many Speedway families and always find options that fit their budget.",
      },
      {
        question: "What types of life insurance are most popular in Speedway?",
        answer: "Term life insurance is the most popular choice for Speedway families. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  anderson: {
    name: "Anderson",
    population: "55,000+",
    county: "Madison County",
    zipCodes: ["46011", "46012", "46013", "46016", "46017"],
    description: "Anderson is the county seat of Madison County and one of central Indiana's largest cities. With a rich manufacturing heritage, a revitalizing downtown, and a strong community spirit, Anderson is home to a diverse mix of working families, retirees, and young professionals. Anderson University adds an educational and cultural dimension to the community. As Anderson continues to evolve, life insurance remains an essential part of financial planning for its residents.",
    neighborhoods: [
      "Downtown Anderson",
      "Anderson University Area",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Mounds State Park Area",
      "Pendleton Pike Corridor",
    ],
    localInsights: [
      "County seat of Madison County and one of central Indiana's largest cities",
      "Median household income: $45,000+ (Madison County)",
      "Homeownership rate: 62% — mortgage protection is important",
      "Average home value: $145,000+ and growing",
      "Home to Anderson University — a private liberal arts university",
      "Rich manufacturing heritage with ongoing economic revitalization",
      "Easy access to I-69 and Indianapolis",
    ],
    faqs: [
      {
        question: "How much life insurance do Anderson families need?",
        answer: "Most Anderson families need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $45,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Anderson, Indiana?",
        answer: "A healthy 35-year-old in Anderson can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Anderson neighborhoods?",
        answer: "Yes — we serve all Anderson neighborhoods and surrounding Madison County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can I get life insurance if I'm a factory worker or blue-collar worker in Anderson?",
        answer: "Absolutely. We work with many blue-collar workers and factory workers in Anderson. Your occupation may affect your rates slightly, but most factory workers qualify for standard or preferred rates. We'll find the best carrier for your specific occupation and health profile.",
      },
      {
        question: "What types of life insurance are most popular in Anderson?",
        answer: "Term life insurance is the most popular choice for Anderson families. Final expense insurance is very popular among Anderson seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  muncie: {
    name: "Muncie",
    population: "65,000+",
    county: "Delaware County",
    zipCodes: ["47302", "47303", "47304", "47305", "47306"],
    description: "Muncie is the county seat of Delaware County and home to Ball State University, one of Indiana's largest universities. Known as the 'Middletown' of America from the famous sociological studies, Muncie is a diverse city with a strong educational heritage, a revitalizing downtown, and a resilient community spirit. With a mix of students, faculty, healthcare workers, and working families, Muncie residents have diverse life insurance needs.",
    neighborhoods: [
      "Ball State University Area",
      "Downtown Muncie",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Minnetrista Area",
      "White River Area",
    ],
    localInsights: [
      "Home to Ball State University — one of Indiana's largest universities",
      "Median household income: $42,000+ (Delaware County)",
      "Homeownership rate: 58% — mortgage protection is important",
      "Average home value: $130,000+ and growing",
      "IU Health Ball Memorial Hospital is a major employer",
      "Rich cultural heritage as the 'Middletown' of America",
      "Easy access to I-69 and Indianapolis",
    ],
    faqs: [
      {
        question: "How much life insurance do Muncie families need?",
        answer: "Most Muncie families need $200,000–$500,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $42,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Muncie?",
        answer: "A healthy 35-year-old in Muncie can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Muncie neighborhoods?",
        answer: "Yes — we serve all Muncie neighborhoods and surrounding Delaware County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can Ball State faculty and staff get life insurance through you?",
        answer: "Absolutely. We work with many Ball State University faculty and staff. Employer-provided life insurance is a great start, but it's typically limited to 1–2x your salary and disappears if you leave the company. We help university employees supplement their employer coverage with affordable private policies that stay with them regardless of employment changes.",
      },
      {
        question: "What types of life insurance are most popular in Muncie?",
        answer: "Term life insurance is the most popular choice for Muncie families. Final expense insurance is very popular among Muncie seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  columbus: {
    name: "Columbus",
    population: "50,000+",
    county: "Bartholomew County",
    zipCodes: ["47201", "47203"],
    description: "Columbus is one of Indiana's most architecturally significant cities, renowned worldwide for its collection of modernist buildings designed by legendary architects. Home to Cummins Inc. and a thriving manufacturing economy, Columbus has a strong, well-educated workforce and a high quality of life. With median household incomes above the state average and a strong culture of community investment, Columbus families understand the importance of protecting their financial future.",
    neighborhoods: [
      "Downtown Columbus",
      "Cummins Area",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Clifty Falls Area",
      "Flatrock River Area",
    ],
    localInsights: [
      "World-renowned for modernist architecture — the 'Athens of the Prairie'",
      "Median household income: $62,000+ (Bartholomew County)",
      "Homeownership rate: 68% — mortgage protection is important",
      "Average home value: $220,000+ and growing",
      "Cummins Inc. is the dominant employer — a Fortune 500 company",
      "Strong manufacturing economy with highly skilled workforce",
      "Bartholomew Consolidated School Corporation rated among top in Indiana",
    ],
    faqs: [
      {
        question: "How much life insurance do Columbus families need?",
        answer: "Most Columbus families need $350,000–$800,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $62,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "Can you help me supplement my Cummins employee life insurance?",
        answer: "Absolutely. We work with many Cummins employees. Employer-provided life insurance is a great start, but it's typically limited to 1–2x your salary and disappears if you leave the company. We help Cummins employees supplement their employer coverage with affordable private policies that stay with them for life, regardless of employment changes.",
      },
      {
        question: "Do you serve all Columbus neighborhoods?",
        answer: "Yes — we serve all Columbus neighborhoods and surrounding Bartholomew County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What's the average cost of life insurance in Columbus, Indiana?",
        answer: "A healthy 35-year-old in Columbus can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "What types of life insurance are most popular in Columbus?",
        answer: "Term life insurance is the most popular choice for Columbus families. Mortgage protection is important for homeowners. Final expense insurance is popular among seniors. High-income Cummins professionals may benefit from permanent policies for tax-advantaged wealth building.",
      },
    ],
  },
  bloomington: {
    name: "Bloomington",
    population: "84,000+",
    county: "Monroe County",
    zipCodes: ["47401", "47403", "47404", "47405", "47406", "47408"],
    description: "Bloomington is home to Indiana University, one of the nation's premier research universities, and is one of Indiana's most vibrant and culturally rich cities. With a diverse population of students, faculty, healthcare workers, and long-time residents, Bloomington has a unique character that blends college-town energy with small-city charm. IU Health Bloomington Hospital is a major employer, and the local economy is anchored by education, healthcare, and technology.",
    neighborhoods: [
      "Indiana University Campus Area",
      "Downtown Bloomington",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Griffy Lake Area",
      "B-Line Trail Corridor",
    ],
    localInsights: [
      "Home to Indiana University — one of the nation's premier research universities",
      "Median household income: $48,000+ (Monroe County)",
      "Homeownership rate: 45% — mix of owners and renters (large student population)",
      "Average home value: $240,000+ and growing",
      "IU Health Bloomington Hospital is a major employer",
      "Strong arts, culture, and dining scene",
      "Monroe County has a highly educated workforce",
    ],
    faqs: [
      {
        question: "How much life insurance do Bloomington families need?",
        answer: "Most Bloomington families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $48,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "Can IU faculty and staff get life insurance through you?",
        answer: "Absolutely. We work with many IU faculty and staff. Employer-provided life insurance is a great start, but it's typically limited to 1–2x your salary. We help IU employees supplement their employer coverage with affordable private policies that stay with them regardless of employment changes.",
      },
      {
        question: "Do you serve all Bloomington neighborhoods?",
        answer: "Yes — we serve all Bloomington neighborhoods and surrounding Monroe County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What's the average cost of life insurance in Bloomington?",
        answer: "A healthy 35-year-old in Bloomington can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "What types of life insurance are most popular in Bloomington?",
        answer: "Term life insurance is the most popular choice for Bloomington families. Mortgage protection is important for homeowners. Final expense insurance is popular among seniors. IU faculty may benefit from permanent policies for tax-advantaged wealth building.",
      },
    ],
  },
  "terre-haute": {
    name: "Terre Haute",
    population: "58,000+",
    county: "Vigo County",
    zipCodes: ["47801", "47802", "47803", "47804", "47805", "47807", "47809"],
    description: "Terre Haute is the county seat of Vigo County and the largest city in western Indiana. Home to Indiana State University and Rose-Hulman Institute of Technology, Terre Haute has a strong educational heritage and a diverse economy anchored by healthcare, education, and manufacturing. As the city continues to revitalize and attract new investment, life insurance remains an essential part of financial planning for Terre Haute families.",
    neighborhoods: [
      "Indiana State University Area",
      "Rose-Hulman Area",
      "Downtown Terre Haute",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Wabash River Area",
    ],
    localInsights: [
      "Home to Indiana State University and Rose-Hulman Institute of Technology",
      "Median household income: $42,000+ (Vigo County)",
      "Homeownership rate: 58% — mortgage protection is important",
      "Average home value: $130,000+ and growing",
      "Union Hospital is a major employer",
      "Strategic location on I-70 between Indianapolis and St. Louis",
      "Strong manufacturing and healthcare economy",
    ],
    faqs: [
      {
        question: "How much life insurance do Terre Haute families need?",
        answer: "Most Terre Haute families need $200,000–$500,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $42,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Terre Haute?",
        answer: "A healthy 35-year-old in Terre Haute can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Terre Haute neighborhoods?",
        answer: "Yes — we serve all Terre Haute neighborhoods and surrounding Vigo County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can ISU or Rose-Hulman faculty and staff get life insurance through you?",
        answer: "Absolutely. We work with many university faculty and staff in Terre Haute. Employer-provided life insurance is a great benefit, but it's typically limited to 1–2x your salary. We help university employees supplement their union coverage with affordable private policies.",
      },
      {
        question: "What types of life insurance are most popular in Terre Haute?",
        answer: "Term life insurance is the most popular choice for Terre Haute families. Final expense insurance is very popular among Terre Haute seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  kokomo: {
    name: "Kokomo",
    population: "57,000+",
    county: "Howard County",
    zipCodes: ["46901", "46902", "46903", "46904"],
    description: "Kokomo is the county seat of Howard County and one of Indiana's most important manufacturing cities, known as the 'City of Firsts' for its many inventions and innovations. Home to major Stellantis (formerly Chrysler) transmission plants and a strong automotive supply chain, Kokomo has a proud working-class heritage and a resilient economy. With a mix of factory workers, healthcare professionals, and small business owners, Kokomo residents have diverse life insurance needs.",
    neighborhoods: [
      "Downtown Kokomo",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Stellantis Plant Area",
      "Indiana University Kokomo Area",
      "Wildcat Creek Area",
    ],
    localInsights: [
      "Known as the 'City of Firsts' — birthplace of many American inventions",
      "Median household income: $48,000+ (Howard County)",
      "Homeownership rate: 65% — mortgage protection is important",
      "Average home value: $155,000+ and growing",
      "Stellantis (Chrysler) transmission plants are the dominant employer",
      "Strong automotive manufacturing economy",
      "Indiana University Kokomo serves local students",
    ],
    faqs: [
      {
        question: "How much life insurance do Kokomo families need?",
        answer: "Most Kokomo families need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $48,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "Can Stellantis workers get life insurance through you?",
        answer: "Absolutely. We work with many Stellantis and automotive workers in Kokomo. Union-provided life insurance is a great benefit, but it's typically limited to 1–2x your salary and may not be portable. We help Kokomo factory workers supplement their union coverage with affordable private policies that stay with them for life.",
      },
      {
        question: "Do you serve all Kokomo neighborhoods?",
        answer: "Yes — we serve all Kokomo neighborhoods and surrounding Howard County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "What's the average cost of life insurance in Kokomo?",
        answer: "A healthy 35-year-old in Kokomo can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "What types of life insurance are most popular in Kokomo?",
        answer: "Term life insurance is the most popular choice for Kokomo families. Final expense insurance is very popular among Kokomo seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  richmond: {
    name: "Richmond",
    population: "35,000+",
    county: "Wayne County",
    zipCodes: ["47374"],
    description: "Richmond is the county seat of Wayne County and the largest city in eastern Indiana, located near the Ohio border. Home to Earlham College and Indiana University East, Richmond has a strong educational heritage and a diverse economy. Known for its beautiful rose gardens and historic downtown, Richmond is a community with deep roots and a strong sense of identity. Life insurance is an essential part of financial planning for Richmond families.",
    neighborhoods: [
      "Downtown Richmond",
      "Earlham College Area",
      "IU East Area",
      "Northside",
      "Southside",
      "Eastside",
      "Westside",
      "Glen Miller Park Area",
    ],
    localInsights: [
      "County seat of Wayne County and largest city in eastern Indiana",
      "Median household income: $42,000+ (Wayne County)",
      "Homeownership rate: 60% — mortgage protection is important",
      "Average home value: $130,000+ and growing",
      "Home to Earlham College and Indiana University East",
      "Known as the 'Rose City' for its beautiful rose gardens",
      "Strategic location on I-70 near the Ohio border",
    ],
    faqs: [
      {
        question: "How much life insurance do Richmond families need?",
        answer: "Most Richmond families need $200,000–$500,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $42,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Richmond, Indiana?",
        answer: "A healthy 35-year-old in Richmond can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Richmond neighborhoods?",
        answer: "Yes — we serve all Richmond neighborhoods and surrounding Wayne County communities. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can Earlham or IU East faculty and staff get life insurance through you?",
        answer: "Absolutely. We work with many university faculty and staff in Richmond. Employer-provided life insurance is a great start, but it's typically limited to 1–2x your salary. We help university employees supplement their employer coverage with affordable private policies.",
      },
      {
        question: "What types of life insurance are most popular in Richmond?",
        answer: "Term life insurance is the most popular choice for Richmond families. Final expense insurance is very popular among Richmond seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation.",
      },
    ],
  },
  franklin: {
    name: "Franklin",
    population: "25,000+",
    county: "Johnson County",
    zipCodes: ["46131"],
    description: "Franklin is the county seat of Johnson County and a growing community south of Indianapolis. Known for Franklin College, a historic liberal arts institution, and a charming downtown square, Franklin blends small-town character with modern suburban growth. With rising home values and a strong young family demographic, Franklin residents need solid life insurance protection to safeguard their financial future.",
    neighborhoods: ["Downtown Franklin", "Franklin College Area", "Northside", "Southside", "Eastside", "Westside", "Rural Johnson County"],
    localInsights: [
      "County seat of Johnson County with a growing population",
      "Home to Franklin College — a private liberal arts institution",
      "Median household income: $65,000+ (Johnson County)",
      "Homeownership rate: 68% — mortgage protection is important",
      "Average home value: $230,000+ and growing",
      "Easy access to I-65 south of Indianapolis",
      "Franklin Community School Corporation serves local families",
    ],
    faqs: [
      { question: "How much life insurance do Franklin families need?", answer: "Most Franklin families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $65,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount." },
      { question: "What's the average cost of life insurance in Franklin, Indiana?", answer: "A healthy 35-year-old in Franklin can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation." },
      { question: "Do you serve all Franklin neighborhoods?", answer: "Yes — we serve all Franklin neighborhoods and surrounding Johnson County communities. We meet clients by phone, video, or in-home appointment — no office visit required." },
      { question: "What types of life insurance are most popular in Franklin?", answer: "Term life insurance is the most popular choice for Franklin families. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation." },
      { question: "Can Franklin College faculty and staff get life insurance through you?", answer: "Absolutely. We work with many college faculty and staff. Employer-provided life insurance is a great start, but it's typically limited to 1–2x your salary. We help university employees supplement their employer coverage with affordable private policies." },
    ],
  },
  cumberland: {
    name: "Cumberland",
    population: "6,000+",
    county: "Marion/Hancock County",
    zipCodes: ["46229"],
    description: "Cumberland is a small town on the east side of Indianapolis, straddling the Marion and Hancock County line. Known for its tight-knit community and affordable housing, Cumberland offers suburban living with easy access to Indianapolis via I-70. As the community grows and home values rise, life insurance is an essential part of financial planning for Cumberland families.",
    neighborhoods: ["Downtown Cumberland", "Northside", "Southside", "I-70 Corridor", "Rural Hancock County"],
    localInsights: [
      "Small community on the east side of Indianapolis",
      "Median household income: $55,000+ (east Marion County)",
      "Homeownership rate: 68% — mortgage protection is important",
      "Average home value: $200,000+ and growing",
      "Easy access to I-70 and downtown Indianapolis",
      "Close proximity to Greenfield and Lawrence",
      "Mt. Vernon Community Schools serves local families",
    ],
    faqs: [
      { question: "How much life insurance do Cumberland families need?", answer: "Most Cumberland families need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $55,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount." },
      { question: "What's the average cost of life insurance in Cumberland, Indiana?", answer: "A healthy 35-year-old in Cumberland can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation." },
      { question: "Do you serve all Cumberland neighborhoods?", answer: "Yes — we serve all Cumberland neighborhoods and surrounding east Indianapolis communities. We meet clients by phone, video, or in-home appointment — no office visit required." },
      { question: "What types of life insurance are most popular in Cumberland?", answer: "Term life insurance is the most popular choice for Cumberland families. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation." },
      { question: "Can I get life insurance quickly in Cumberland?", answer: "Yes. Many carriers offer same-day or next-day approval for healthy applicants. No-medical-exam policies can be approved in 24–48 hours. We'll recommend the fastest path based on your health profile and coverage needs." },
    ],
  },
  greenfield: {
    name: "Greenfield",
    population: "22,000+",
    county: "Hancock County",
    zipCodes: ["46140"],
    description: "Greenfield is the county seat of Hancock County and the birthplace of poet James Whitcomb Riley. Located along I-70 east of Indianapolis, Greenfield is a growing community with affordable housing, excellent schools, and a strong agricultural heritage. As Greenfield continues to attract new residents and businesses, life insurance is an essential part of financial planning for its families.",
    neighborhoods: ["Downtown Greenfield", "Riley Park Area", "Northside", "Southside", "Eastside", "Westside", "Rural Hancock County"],
    localInsights: [
      "County seat of Hancock County and birthplace of James Whitcomb Riley",
      "Median household income: $62,000+ (Hancock County)",
      "Homeownership rate: 70% — mortgage protection is important",
      "Average home value: $220,000+ and growing",
      "Easy access to I-70 east of Indianapolis",
      "Greenfield-Central Community Schools serves local families",
      "Strong agricultural and manufacturing economy",
    ],
    faqs: [
      { question: "How much life insurance do Greenfield families need?", answer: "Most Greenfield families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $62,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount." },
      { question: "What's the average cost of life insurance in Greenfield, Indiana?", answer: "A healthy 35-year-old in Greenfield can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation." },
      { question: "Do you serve all Greenfield neighborhoods?", answer: "Yes — we serve all Greenfield neighborhoods and surrounding Hancock County communities. We meet clients by phone, video, or in-home appointment — no office visit required." },
      { question: "What types of life insurance are most popular in Greenfield?", answer: "Term life insurance is the most popular choice for Greenfield families. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation." },
      { question: "Can I get life insurance if I'm a farmer or agricultural worker in Greenfield?", answer: "Absolutely. We work with many farmers and agricultural workers in Hancock County. Your occupation may affect your rates slightly, but most agricultural workers qualify for standard or preferred rates. We'll find the best carrier for your specific occupation and health profile." },
    ],
  },
  danville: {
    name: "Danville",
    population: "10,000+",
    county: "Hendricks County",
    zipCodes: ["46122"],
    description: "Danville is the county seat of Hendricks County, known for its charming historic courthouse square and small-town atmosphere. Located just west of Indianapolis, Danville offers affordable housing and a peaceful community feel while remaining within easy commuting distance of the city. As Hendricks County continues to grow, Danville families are recognizing the importance of life insurance to protect their homes and loved ones.",
    neighborhoods: ["Downtown Danville", "Courthouse Square Area", "Northside", "Southside", "Eastside", "Westside", "Rural Hendricks County"],
    localInsights: [
      "County seat of Hendricks County with a charming historic courthouse square",
      "Median household income: $72,000+ (Hendricks County)",
      "Homeownership rate: 72% — mortgage protection is important",
      "Average home value: $240,000+ and growing",
      "Easy access to US-36 and I-74 west of Indianapolis",
      "Danville Community School Corporation serves local families",
      "Strong agricultural and small business economy",
    ],
    faqs: [
      { question: "How much life insurance do Danville families need?", answer: "Most Danville families need $300,000–$700,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $72,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount." },
      { question: "What's the average cost of life insurance in Danville, Indiana?", answer: "A healthy 35-year-old in Danville can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation." },
      { question: "Do you serve all Danville neighborhoods?", answer: "Yes — we serve all Danville neighborhoods and surrounding Hendricks County communities. We meet clients by phone, video, or in-home appointment — no office visit required." },
      { question: "What types of life insurance are most popular in Danville?", answer: "Term life insurance is the most popular choice for Danville families. Final expense insurance is popular among seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation." },
      { question: "Can I get life insurance if I'm self-employed or a small business owner in Danville?", answer: "Absolutely. We work with many self-employed individuals and small business owners in Hendricks County. Self-employed people often have no employer-provided life insurance, making private coverage even more important. We can help you find affordable coverage that fits your income and budget." },
    ],
  },
  martinsville: {
    name: "Martinsville",
    population: "12,000+",
    county: "Morgan County",
    zipCodes: ["46151"],
    description: "Martinsville is the county seat of Morgan County, located south of Indianapolis along SR-37. Known for its artesian wells, historic downtown, and strong community spirit, Martinsville is a growing community that blends small-town charm with modern amenities. As Morgan County continues to attract new residents, life insurance is an essential part of financial planning for Martinsville families.",
    neighborhoods: ["Downtown Martinsville", "Artesian Well Area", "Northside", "Southside", "Eastside", "Westside", "Rural Morgan County"],
    localInsights: [
      "County seat of Morgan County with a historic downtown",
      "Median household income: $55,000+ (Morgan County)",
      "Homeownership rate: 65% — mortgage protection is important",
      "Average home value: $185,000+ and growing",
      "Easy access to SR-37 and I-69 south of Indianapolis",
      "Martinsville Community School Corporation serves local families",
      "Known for its historic artesian wells and community heritage",
    ],
    faqs: [
      { question: "How much life insurance do Martinsville families need?", answer: "Most Martinsville families need $250,000–$600,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $55,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount." },
      { question: "What's the average cost of life insurance in Martinsville, Indiana?", answer: "A healthy 35-year-old in Martinsville can get $500,000 of 20-year term life insurance for $25–$35/month. Rates vary based on age, health, and coverage amount. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation." },
      { question: "Do you serve all Martinsville neighborhoods?", answer: "Yes — we serve all Martinsville neighborhoods and surrounding Morgan County communities. We meet clients by phone, video, or in-home appointment — no office visit required." },
      { question: "What types of life insurance are most popular in Martinsville?", answer: "Term life insurance is the most popular choice for Martinsville families. Final expense insurance is very popular among Martinsville seniors. Mortgage protection is important for homeowners. We'll help you find the right type and amount for your specific situation." },
      { question: "Can I get affordable life insurance in Martinsville?", answer: "Absolutely. Life insurance is more affordable than most people think. A $250,000 policy can cost as little as $15–$20/month for a healthy 35-year-old. We work with many Martinsville families and always find options that fit their budget." },
    ],
  },
  "fort-wayne": {
    name: "Fort Wayne",
    population: "270,000+",
    county: "Allen County",
    zipCodes: ["46801", "46802", "46803", "46804", "46805", "46806", "46807", "46808", "46809", "46814", "46815", "46816", "46818", "46819", "46825", "46835", "46845"],
    description: "Fort Wayne is Indiana's second-largest city and the economic hub of northeastern Indiana. With a diverse economy anchored by healthcare, manufacturing, financial services, and defense, Fort Wayne offers a high quality of life at an affordable cost. From the vibrant downtown riverfront to the family-friendly suburbs of southwest Fort Wayne, the city has something for everyone. Fort Wayne families are building real wealth — and they need the right life insurance protection to safeguard it.",
    neighborhoods: [
      "Downtown Fort Wayne",
      "Southwest Fort Wayne",
      "Northwest Fort Wayne",
      "Northeast Fort Wayne",
      "Southeast Fort Wayne",
      "Aboite Township",
      "St. Joe Road Corridor",
      "Dupont Road Corridor",
      "Covington Road Corridor",
      "Lima Road Corridor",
      "Waynedale",
      "New Haven",
    ],
    localInsights: [
      "Indiana's second-largest city and northeastern Indiana's economic hub",
      "Median household income: $55,000+ (Allen County)",
      "Homeownership rate: 65% — mortgage protection is important",
      "Average home value: $200,000+ and growing",
      "Major employers: Parkview Health, Lutheran Health Network, Lincoln Financial, Steel Dynamics",
      "Strong manufacturing, healthcare, and financial services economy",
      "Fort Wayne Community Schools and multiple suburban districts serve local families",
    ],
    faqs: [
      {
        question: "How much life insurance do Fort Wayne families need?",
        answer: "Most Fort Wayne families need $350,000–$800,000 in coverage to protect their mortgage, replace lost income, and fund their children's education. With median household incomes around $55,000, a standard guideline is 10–12x your annual income. We offer a free Financial Needs Analysis to calculate your precise coverage amount.",
      },
      {
        question: "What's the average cost of life insurance in Fort Wayne?",
        answer: "A healthy 35-year-old in Fort Wayne can get $500,000 of 20-year term life insurance for $25–$35/month. A 45-year-old in good health might pay $60–$90/month for the same coverage. As an independent agency, we shop dozens of carriers to find the most competitive rate for your specific situation.",
      },
      {
        question: "Do you serve all Fort Wayne neighborhoods?",
        answer: "Yes — we serve all Fort Wayne neighborhoods and surrounding Allen County communities, including Aboite Township, New Haven, and all suburban areas. We meet clients by phone, video, or in-home appointment — no office visit required.",
      },
      {
        question: "Can Parkview or Lutheran Health Network employees get life insurance through you?",
        answer: "Absolutely. We work with many healthcare workers in Fort Wayne. Employer-provided life insurance is a great benefit, but it's typically limited to 1–2x your salary. We help Fort Wayne healthcare professionals supplement their employer coverage with affordable private policies that stay with them regardless of employment changes.",
      },
      {
        question: "What types of life insurance are most popular in Fort Wayne?",
        answer: "Term life insurance is the most popular choice for Fort Wayne families. Mortgage protection is important for homeowners. Final expense insurance is popular among seniors. High-income professionals at Lincoln Financial or Steel Dynamics may benefit from permanent policies for tax-advantaged wealth building.",
      },
      {
        question: "Do you work with Fort Wayne business owners for key person insurance?",
        answer: "Yes — Fort Wayne has a strong small business community, and key person life insurance is an important tool for business continuity. If your business depends on one or two key individuals, key person insurance ensures the business can survive and recover if something happens to them. We work with Fort Wayne business owners to design the right coverage strategy.",
      },
    ],
  },
};

// City-specific OG images for social sharing — unique per city
const cityOgImages: Record<string, string> = {
  carmel: 'https://public.readdy.ai/ai/img_res/e4cf94da18d3aafe540f3f0f11c17762.jpg',
  fishers: 'https://readdy.ai/api/search-image?query=thriving%20suburban%20community%20Fishers%20Indiana%20modern%20homes%20young%20families%20park%20Saxony%20neighborhood%20Hamilton%20County%20vibrant%20residential%20area%20blue%20sky&width=1200&height=630&seq=city-og-fishers-v1&orientation=landscape',
  indianapolis: 'https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20skyline%20downtown%20Monument%20Circle%20State%20Capitol%20aerial%20view%20urban%20cityscape%20Midwest%20city%20warm%20golden%20light%20professional%20photography&width=1200&height=630&seq=city-og-indianapolis-v1&orientation=landscape',
  westfield: 'https://readdy.ai/api/search-image?query=growing%20suburban%20neighborhood%20Westfield%20Indiana%20Grand%20Park%20area%20new%20homes%20family%20community%20Hamilton%20County%20residential%20development%20blue%20sky%20sunny%20day&width=1200&height=630&seq=city-og-westfield-v1&orientation=landscape',
  noblesville: 'https://readdy.ai/api/search-image?query=charming%20historic%20downtown%20Noblesville%20Indiana%20courthouse%20square%20brick%20buildings%20small%20town%20community%20Hamilton%20County%20Morse%20Reservoir%20waterfront%20warm%20light&width=1200&height=630&seq=city-og-noblesville-v1&orientation=landscape',
  brownsburg: 'https://readdy.ai/api/search-image?query=family-friendly%20suburban%20neighborhood%20Brownsburg%20Indiana%20Hendricks%20County%20residential%20homes%20tree-lined%20streets%20community%20feel%20warm%20sunny%20afternoon&width=1200&height=630&seq=city-og-brownsburg-v1&orientation=landscape',
  zionsville: 'https://readdy.ai/api/search-image?query=picturesque%20brick%20paved%20Main%20Street%20Zionsville%20Indiana%20charming%20village%20shops%20boutiques%20affluent%20community%20Boone%20County%20historic%20downtown%20warm%20light&width=1200&height=630&seq=city-og-zionsville-v1&orientation=landscape',
  whitestown: 'https://readdy.ai/api/search-image?query=fast%20growing%20new%20residential%20development%20Whitestown%20Indiana%20Boone%20County%20modern%20homes%20new%20construction%20young%20families%20community%20warm%20sunny%20day%20blue%20sky&width=1200&height=630&seq=city-og-whitestown-v1&orientation=landscape',
  lebanon: 'https://readdy.ai/api/search-image?query=small%20town%20community%20Lebanon%20Indiana%20Boone%20County%20courthouse%20historic%20downtown%20residential%20neighborhoods%20affordable%20homes%20warm%20sunny%20day%20Midwest%20charm&width=1200&height=630&seq=city-og-lebanon-v1&orientation=landscape',
  mooresville: 'https://readdy.ai/api/search-image?query=quiet%20suburban%20community%20Mooresville%20Indiana%20Morgan%20County%20residential%20neighborhood%20family%20homes%20affordable%20living%20southwest%20Indianapolis%20warm%20light&width=1200&height=630&seq=city-og-mooresville-v1&orientation=landscape',
  castleton: 'https://readdy.ai/api/search-image?query=northeast%20Indianapolis%20Castleton%20Indiana%20suburban%20retail%20residential%20mixed%20community%20comfortable%20family%20homes%20near%20highway%20access%20blue%20sky%20warm%20sunny%20day&width=1200&height=630&seq=city-og-castleton-v1&orientation=landscape',
  geist: 'https://readdy.ai/api/search-image?query=beautiful%20lakeside%20luxury%20homes%20Geist%20Reservoir%20Indianapolis%20Indiana%20upscale%20waterfront%20community%20boats%20docks%20blue%20water%20affluent%20neighborhood%20warm%20golden%20light&width=1200&height=630&seq=city-og-geist-v1&orientation=landscape',
  'broad-ripple': 'https://readdy.ai/api/search-image?query=vibrant%20eclectic%20Broad%20Ripple%20neighborhood%20Indianapolis%20Indiana%20arts%20scene%20restaurants%20pedestrians%20White%20River%20Greenway%20Monon%20Trail%20urban%20village%20colorful%20warm%20light&width=1200&height=630&seq=city-og-broad-ripple-v1&orientation=landscape',
  greenwood: 'https://readdy.ai/api/search-image?query=growing%20suburb%20Greenwood%20Indiana%20Johnson%20County%20residential%20neighborhood%20Center%20Grove%20family%20homes%20community%20comfortable%20affordable%20south%20Indianapolis%20warm%20day&width=1200&height=630&seq=city-og-greenwood-v1&orientation=landscape',
  avon: 'https://readdy.ai/api/search-image?query=thriving%20suburban%20community%20Avon%20Indiana%20Hendricks%20County%20family%20neighborhood%20new%20homes%20residential%20development%20warm%20sunny%20day%20blue%20sky%20young%20families%20community&width=1200&height=630&seq=city-og-avon-v1&orientation=landscape',
  plainfield: 'https://readdy.ai/api/search-image?query=suburban%20neighborhood%20Plainfield%20Indiana%20Hendricks%20County%20near%20Indianapolis%20airport%20residential%20community%20family%20homes%20comfortable%20affordable%20warm%20afternoon%20light&width=1200&height=630&seq=city-og-plainfield-v1&orientation=landscape',
  lawrence: 'https://readdy.ai/api/search-image?query=northeast%20Indianapolis%20Lawrence%20Indiana%20Marion%20County%20suburban%20neighborhood%20Fort%20Harrison%20State%20Park%20residential%20community%20diverse%20families%20homes%20warm%20light&width=1200&height=630&seq=city-og-lawrence-v1&orientation=landscape',
  'beech-grove': 'https://readdy.ai/api/search-image?query=close-knit%20neighborhood%20Beech%20Grove%20Indiana%20working%20class%20community%20Marion%20County%20residential%20homes%20Main%20Street%20community%20pride%20warm%20sunny%20afternoon&width=1200&height=630&seq=city-og-beech-grove-v1&orientation=landscape',
  speedway: 'https://readdy.ai/api/search-image?query=Speedway%20Indiana%20Indianapolis%20Motor%20Speedway%20small%20town%20community%20neighborhood%20residential%20homes%20American%20flag%20community%20pride%20warm%20light%20blue%20sky&width=1200&height=630&seq=city-og-speedway-v1&orientation=landscape',
  anderson: 'https://readdy.ai/api/search-image?query=Madison%20County%20city%20Anderson%20Indiana%20downtown%20revitalization%20community%20center%20residential%20neighborhoods%20working%20families%20warm%20sunny%20day%20Midwest%20charm&width=1200&height=630&seq=city-og-anderson-v1&orientation=landscape',
  muncie: 'https://readdy.ai/api/search-image?query=Ball%20State%20University%20Muncie%20Indiana%20downtown%20community%20Delaware%20County%20residential%20neighborhood%20campus%20area%20warm%20light%20blue%20sky%20Midwest%20city&width=1200&height=630&seq=city-og-muncie-v1&orientation=landscape',
  columbus: 'https://readdy.ai/api/search-image?query=Columbus%20Indiana%20modern%20architecture%20iconic%20Cummins%20buildings%20downtown%20city%20Bartholomew%20County%20community%20warm%20light%20professional%20sophisticated%20Midwest%20city&width=1200&height=630&seq=city-og-columbus-v1&orientation=landscape',
  bloomington: 'https://readdy.ai/api/search-image?query=Indiana%20University%20campus%20Bloomington%20Indiana%20vibrant%20college%20town%20community%20Monroe%20County%20limestone%20buildings%20warm%20autumn%20light%20beautiful%20campus%20walkways&width=1200&height=630&seq=city-og-bloomington-v1&orientation=landscape',
  'terre-haute': 'https://readdy.ai/api/search-image?query=Terre%20Haute%20Indiana%20Vigo%20County%20community%20downtown%20Wabash%20River%20western%20Indiana%20residential%20neighborhoods%20warm%20light%20Midwest%20city%20community%20spirit&width=1200&height=630&seq=city-og-terre-haute-v1&orientation=landscape',
  kokomo: 'https://readdy.ai/api/search-image?query=Kokomo%20Indiana%20Howard%20County%20community%20downtown%20manufacturing%20city%20residential%20neighborhoods%20warm%20sunny%20day%20blue%20sky%20Midwest%20working%20community%20pride&width=1200&height=630&seq=city-og-kokomo-v1&orientation=landscape',
  richmond: 'https://readdy.ai/api/search-image?query=Richmond%20Indiana%20Wayne%20County%20Earlham%20College%20rose%20garden%20downtown%20Glen%20Miller%20Park%20community%20warm%20light%20eastern%20Indiana%20city%20neighborhood%20residential&width=1200&height=630&seq=city-og-richmond-v1&orientation=landscape',
  'fort-wayne': 'https://readdy.ai/api/search-image?query=Fort%20Wayne%20Indiana%20second%20largest%20city%20Allen%20County%20downtown%20riverfront%20modern%20skyline%20community%20family%20residential%20neighborhoods%20warm%20golden%20light%20Midwest&width=1200&height=630&seq=city-og-fort-wayne-v1&orientation=landscape',
};

const DEFAULT_CITY_OG_IMAGE = 'https://readdy.ai/api/search-image?query=Indiana%20suburban%20neighborhood%20family%20life%20insurance%20community%20residential%20homes%20warm%20sunny%20day%20blue%20sky%20comfortable%20Midwest%20living%20financial%20protection&width=1200&height=630&seq=city-og-default-v1&orientation=landscape';

export default function CityLandingPage() {
  const { citySlug } = useParams<{ citySlug: string }>();

  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

  // Cities that have dedicated, more-authoritative standalone pages.
  // The /service-areas/:slug version points canonical to the dedicated URL so
  // Google consolidates ranking signals onto the better page.
  const DEDICATED_CANONICAL_MAP: Record<string, string> = {
    fishers:           `${SITE_URL}/life-insurance-fishers`,
    noblesville:       `${SITE_URL}/life-insurance-noblesville`,
    carmel:            `${SITE_URL}/life-insurance-carmel`,
    'hamilton-county': `${SITE_URL}/life-insurance-hamilton-county`,
    'fort-wayne':      `${SITE_URL}/life-insurance-fort-wayne`,
    'south-bend':      `${SITE_URL}/life-insurance-south-bend`,
    evansville:        `${SITE_URL}/life-insurance-evansville`,
  };
  const canonicalUrl = citySlug
    ? (DEDICATED_CANONICAL_MAP[citySlug] ?? `${SITE_URL}/service-areas/${citySlug}`)
    : `${SITE_URL}/service-areas/`;

  const city = citySlug ? cityData[citySlug] : null;
  const geo = citySlug ? cityGeo[citySlug] : null;
  const nearby = citySlug ? nearbyCities[citySlug] : null;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    zip: '',
    insuranceType: '',
    bestTime: '',
    city_name: citySlug || ''
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      formBody.append('sms_consent', smsConsent ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d6q5mgcddmmni7ck8cb0', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          zip: '',
          insuranceType: '',
          bestTime: '',
          city_name: citySlug || ''
        });
        setSmsConsent(false);
      } else {
        setSubmitError('Something went wrong. Please try again or call us at (317) 648-2983.');
      }
    } catch (error) {
      setSubmitError('Something went wrong. Please try again or call us at (317) 648-2983.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build schema objects at render time
  const citySchema = city && geo ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'InsuranceAgency',
        '@id': `${SITE_URL}/#organization`,
        'name': 'Hoosier Life Insurance',
        'url': SITE_URL,
        'telephone': '+13176482983',
        'areaServed': [
          {
            '@type': 'City',
            'name': city.name,
            'containedInPlace': {
              '@type': 'State',
              'name': 'Indiana',
              'address': {
                '@type': 'PostalAddress',
                'addressRegion': 'IN',
                'addressCountry': 'US'
              }
            },
          },
          {
            '@type': 'State',
            'name': 'Indiana',
            'address': {
              '@type': 'PostalAddress',
              'addressRegion': 'IN',
              'addressCountry': 'US'
            }
          }
        ],
        'serviceType': [
          'Term Life Insurance',
          'Whole Life Insurance',
          'Universal Life Insurance',
          'Final Expense Insurance',
          'Mortgage Protection Insurance',
          'No Medical Exam Life Insurance'
        ],
        'priceRange': '$$',
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': geo.lat,
          'longitude': geo.lng
        }
      },
      {
        '@type': 'FAQPage',
        'mainEntity': city.faqs.map((faq) => ({
          '@type': 'Question',
          'name': faq.question,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': faq.answer
          }
        }))
      },
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': SITE_URL
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': 'Service Areas',
            'item': `${SITE_URL}/service-areas`
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': city.name,
            'item': `${SITE_URL}/service-areas/${citySlug}`
          }
        ]
      }
    ]
  } : null;

  // Inject SEO meta tags
  useEffect(() => {
    if (!city || !citySlug) return;

    const title = `Life Insurance ${city.name} | Hoosier Life Insurance`;
    const description = `Life insurance in ${city.name}, Indiana — term, whole life, final expense & mortgage protection. Free quotes. Call (317) 648-2983.`;
    const canonical = DEDICATED_CANONICAL_MAP[citySlug] ?? `${SITE_URL}/service-areas/${citySlug}`;
    const ogImage = cityOgImages[citySlug] || DEFAULT_CITY_OG_IMAGE;

    // Set title
    document.title = title;

    // Create or update meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: `life insurance ${city.name}, ${city.name} life insurance agent, term life insurance ${city.name}, whole life insurance ${city.name}, ${city.county} life insurance, life insurance ${city.zipCodes[0]}` },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
      { name: 'geo.position', content: geo ? `${geo.lat};${geo.lng}` : '' },
      { name: 'geo.placename', content: `${city.name}, Indiana` },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const createdElements: HTMLMetaElement[] = [];

    metaTags.forEach(({ name, property, content }) => {
      if (!content) return;
      
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      
      if (element) {
        element.content = content;
      } else {
        element = document.createElement('meta');
        if (name) element.name = name;
        if (property) element.setAttribute('property', property);
        element.content = content;
        document.head.appendChild(element);
        createdElements.push(element);
      }
    });

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = canonical;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonical;
      document.head.appendChild(canonicalLink);
      createdElements.push(canonicalLink as any);
    }

    // Inject Schema.org JSON-LD
    let schemaScript = document.getElementById('city-schema-jsonld') as HTMLScriptElement;
    if (citySchema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'city-schema-jsonld';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
        createdElements.push(schemaScript as any);
      }
      schemaScript.textContent = JSON.stringify(citySchema);
    }

    // Cleanup
    return () => {
      createdElements.forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [city, citySlug, geo, SITE_URL, citySchema, cityOgImages, DEFAULT_CITY_OG_IMAGE]);

  return (
    <div className="bg-white">
      {/* ── SEO HEAD TAGS — rendered at component level so Googlebot sees them immediately ── */}
      {city && citySlug && (
        <>
          <title>{`Life Insurance ${city.name} Indiana | Hoosier Life Insurance`}</title>
          <meta name="description" content={`Life insurance in ${city.name}, Indiana — term, whole life, final expense & mortgage protection. Free quotes. Call (317) 648-2983.`} />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content={`Life Insurance ${city.name} Indiana | Hoosier Life Insurance`} />
          <meta property="og:description" content={`Life insurance in ${city.name}, Indiana — term, whole life, final expense & mortgage protection. Free quotes. Call (317) 648-2983.`} />
          <meta property="og:url" content={`${SITE_URL}/service-areas/${citySlug}`} />
          <meta property="og:type" content="website" />
          <meta name="geo.placename" content={`${city.name}, Indiana`} />
          <meta name="geo.region" content="US-IN" />
          {geo && <meta name="geo.position" content={`${geo.lat};${geo.lng}`} />}
          <meta name="keywords" content={`life insurance ${city.name}, ${city.name} life insurance agent, term life insurance ${city.name}, whole life insurance ${city.name}, ${city.county} life insurance, life insurance ${city.zipCodes[0]}`} />
        </>
      )}
      <MobileStickyTopBar quoteHref="#quote-form" cityName={city?.name} />
      <Header />

      {/* If city not found, redirect */}
      {!city && <Navigate to="/service-areas" replace />}

      {/* Hero Section */}
      <section className="pt-[100px] md:pt-[140px] pb-16 md:pb-24 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D97642]/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D97642]/8 rounded-full blur-[100px]"></div>
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-[13px] text-white/60 mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-[14px]"></i>
            <a href="/service-areas" className="hover:text-white transition-colors">Service Areas</a>
            <i className="ri-arrow-right-s-line text-[14px]"></i>
            <span className="text-white/90">{city?.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left — Content */}
            <div>
              {/* City-specific offer badge for top 5 cities */}
              {citySlug && cityOffers[citySlug] ? (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/20 text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest rounded-full mb-4 border border-[#D97642]/30">
                  <i className="ri-award-fill text-[13px]"></i>
                  {cityOffers[citySlug].badge}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/20 text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest rounded-full mb-4 border border-[#D97642]/30">
                  <i className="ri-map-pin-fill text-[13px]"></i>
                  {city?.county}
                </span>
              )}

              {/* Headline — city-specific for top 5, default otherwise */}
              <h1 className="text-[36px] sm:text-[44px] md:text-[52px] font-bold text-white leading-[1.15] mb-3">
                {citySlug && cityOffers[citySlug]
                  ? cityOffers[citySlug].headline
                  : `Life Insurance in ${city?.name} — Independent Agent, Indiana`
                }
              </h1>

              {/* Subheadline — city-specific for top 5 */}
              {citySlug && cityOffers[citySlug] && (
                <p className="text-[18px] text-[#F0A070] font-semibold mb-3 leading-snug">
                  {cityOffers[citySlug].subheadline}
                </p>
              )}

              <p className="text-[16px] sm:text-[18px] text-white/75 leading-[1.7] mb-5">
                {city?.description}
              </p>

              {/* City-specific offer highlight banner for top 5 */}
              {citySlug && cityOffers[citySlug] && (
                <div className="bg-[#D97642]/20 border border-[#D97642]/40 rounded-xl px-5 py-4 mb-6 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-gift-fill text-white text-[15px]"></i>
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-[#F0A070] leading-tight mb-0.5">
                      {cityOffers[citySlug].offer}
                    </p>
                    <p className="text-[13px] text-white/75 leading-[1.5]">
                      {cityOffers[citySlug].offerDetail}
                    </p>
                  </div>
                </div>
              )}

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                {citySlug && cityOffers[citySlug] ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-2-line text-[15px] text-[#D97642]"></i>
                    </div>
                    <span className="text-[13px] text-white/90 font-medium">{cityOffers[citySlug].highlight}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-phone-line text-[15px] text-[#D97642]"></i>
                      </div>
                      <span className="text-[13px] text-white/90 font-medium">Phone &amp; Video Consultations</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-home-heart-line text-[15px] text-[#D97642]"></i>
                      </div>
                      <span className="text-[13px] text-white/90 font-medium">In-Home Appointments</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-time-line text-[15px] text-[#D97642]"></i>
                      </div>
                      <span className="text-[13px] text-white/90 font-medium">Response in 24 Hours</span>
                    </div>
                  </>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+13176482983"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#D97642] text-white text-[15px] font-semibold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-xl shadow-md cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-[18px]"></i>
                  Call Now
                </a>
                <a
                  href="/wealth-strategy-call"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/15 text-white text-[15px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-[18px]"></i>
                  Book Free Call
                </a>
              </div>
            </div>

            {/* Right — Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D97642]/20 to-transparent rounded-[24px]"></div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20life%20insurance%20agent%20meeting%20with%20a%20happy%20affluent%20family%20in%20a%20modern%20Indiana%20home%2C%20warm%20natural%20lighting%20streaming%20through%20large%20windows%2C%20family%20of%20four%20sitting%20comfortably%20in%20a%20bright%20living%20room%20discussing%20financial%20planning%20documents%2C%20agent%20in%20business%20casual%20attire%20showing%20tablet%20with%20charts%2C%20cozy%20and%20trustworthy%20atmosphere%2C%20professional%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20warm%20color%20tones%20with%20cream%20and%20navy%20accents%2C%20upscale%20suburban%20home%20interior&width=800&height=500&seq=hero-city-landing-family-001&orientation=landscape"
                alt={`Life Insurance Agent with ${city?.name ?? ''} Indiana Family`}
                title={`Trusted Life Insurance Services in ${city?.name ?? ''}, Indiana`}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={800}
                height={500}
                className="w-full h-[400px] object-cover object-top rounded-[24px] shadow-[0_20px_40px_rgba(0,0,0,0.30)] relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Insights Section */}
      <section className="py-12 md:py-16 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-group-fill text-[24px] text-[#D97642]"></i>
              </div>
              <div className="text-[28px] font-bold text-[#1A3A52] mb-1">{city?.population}</div>
              <div className="text-[14px] text-[#4A5568]">Population</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-2-fill text-[24px] text-[#D97642]"></i>
              </div>
              <div className="text-[28px] font-bold text-[#1A3A52] mb-1">{city?.county}</div>
              <div className="text-[14px] text-[#4A5568]">County</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-mail-fill text-[24px] text-[#D97642]"></i>
              </div>
              <div className="text-[28px] font-bold text-[#1A3A52] mb-1">{city?.zipCodes.length}</div>
              <div className="text-[14px] text-[#4A5568]">ZIP Codes Served</div>
            </div>
          </div>

          {/* Why Life Insurance in [City] Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
            <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6">
              <i className="ri-shield-star-line text-[#D97642] mr-2"></i>
              Why Life Insurance in {city?.name}?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {city?.localInsights.map((insight, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-[15px] text-[#D97642]"></i>
                  </div>
                  <p className="text-[15px] text-[#4A5568] leading-[1.6]">{insight}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Neighborhoods Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-8">
            <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6">
              <i className="ri-community-line text-[#D97642] mr-2"></i>
              Top Neighborhoods We Serve in {city?.name}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {city?.neighborhoods.map((neighborhood, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-3 py-2.5 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[14px] text-[#1A3A52] hover:bg-[#D97642]/10 hover:border-[#D97642]/40 transition-all"
                >
                  <i className="ri-map-pin-line text-[15px] text-[#D97642] flex-shrink-0"></i>
                  <span className="font-medium">{neighborhood}</span>
                </div>
              ))}
            </div>
            <p className="text-[14px] text-[#4A5568] mt-6 leading-[1.7]">
              We proudly serve all {city?.name} neighborhoods and surrounding {city?.county} communities. Whether you live in {city?.neighborhoods[0]}, {city?.neighborhoods[1]}, or anywhere else in {city?.name}, we're here to help you find affordable life insurance protection for your family.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-4">
              Life Insurance Solutions for {city?.name} Residents
            </h2>
            <p className="text-[16px] text-[#4A5568] max-w-[700px] mx-auto">
              Affordable, straightforward protection designed for families in {city?.neighborhoods[0]}, {city?.neighborhoods[1]}, and throughout {city?.name}.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-shield-check-fill',
                title: `Term Life Insurance in ${city?.name}`,
                desc: `Maximum coverage for the lowest cost — perfect for protecting your ${city?.name} home and family income.`,
                link: '/term-life',
                cta: `Get Term Life in ${city?.name}`,
              },
              {
                icon: 'ri-heart-pulse-fill',
                title: `Whole Life Insurance in ${city?.name}`,
                desc: 'Lifelong coverage with guaranteed cash value growth and tax advantages.',
                link: '/whole-life',
                cta: `Get Whole Life in ${city?.name}`,
              },
              {
                icon: 'ri-home-heart-fill',
                title: `Mortgage Protection in ${city?.name}`,
                desc: `Ensure your ${city?.name} family never loses their home if something happens to you.`,
                link: '/mortgage-protection',
                cta: `Protect Your ${city?.name} Home`,
              },
              {
                icon: 'ri-user-heart-fill',
                title: `Final Expense Insurance in ${city?.name}`,
                desc: `Affordable coverage for ${city?.name} seniors to cover funeral costs and final expenses.`,
                link: '/final-expense',
                cta: `Final Expense in ${city?.name}`,
              },
              {
                icon: 'ri-stethoscope-line',
                title: `No Medical Exam Coverage in ${city?.name}`,
                desc: 'Get approved in 48 hours without needles, blood tests, or doctor visits.',
                link: '/no-medical-exam',
                cta: `No-Exam Quote in ${city?.name}`,
              },
              {
                icon: 'ri-funds-fill',
                title: `Annuities for ${city?.name} Retirees`,
                desc: 'Secure a guaranteed income stream for retirement — never outlive your money.',
                link: '/annuities',
                cta: `Annuity Strategy in ${city?.name}`,
              },
            ].map((service, idx) => (
              <a
                key={idx}
                href={service.link}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 bg-[#D97642] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-[24px] text-white`}></i>
                </div>
                <h3 className="text-[17px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors leading-snug">
                  {service.title}
                </h3>
                <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-4">{service.desc}</p>
                <div className="flex items-center gap-1">
                  <span className="text-[13px] text-[#D97642] font-semibold">{service.cta}</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            ))}
          </div>

          {/* Product-City Quick Links Bar */}
          <div className="mt-10 bg-[#FDF6F0] rounded-2xl p-6 border border-[#D97642]/20">
            <p className="text-[13px] font-semibold text-[#1A3A52] uppercase tracking-wider mb-4 text-center">
              Quick Links — {city?.name} Insurance by Type
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { label: `Term Life — ${city?.name}`, href: '/term-life', icon: 'ri-calendar-check-line' },
                { label: `Whole Life — ${city?.name}`, href: '/whole-life', icon: 'ri-infinity-line' },
                { label: `Final Expense — ${city?.name}`, href: '/final-expense', icon: 'ri-hand-heart-line' },
                { label: `Mortgage Protection — ${city?.name}`, href: '/mortgage-protection', icon: 'ri-home-heart-line' },
                { label: `No Exam — ${city?.name}`, href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
                { label: `Annuities — ${city?.name}`, href: '/annuities', icon: 'ri-line-chart-line' },
                { label: `IUL — ${city?.name}`, href: '/indexed-universal-life', icon: 'ri-funds-box-line' },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-full text-[12px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap"
                >
                  <i className={`${item.icon} text-[13px]`}></i>
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[700px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-4">
              Get Your Free {city?.name} Quote
            </h2>
            <p className="text-[16px] text-[#4A5568]">
              Fill out the form below and we'll contact you within 24 hours with personalized options — no pressure, no obligation.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-5">
                  <i className="ri-check-double-line text-[32px] text-green-600"></i>
                </div>
                <h3 className="text-[22px] font-bold text-[#1A3A52] mb-3">You're All Set!</h3>
                <p className="text-[15px] text-[#4A5568] leading-[1.7] mb-6 max-w-[400px]">
                  Thanks for reaching out! We'll review your info and contact you within 24 hours with personalized {city?.name} life insurance options.
                </p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="px-6 py-3 bg-[#D97642] text-white text-[14px] font-semibold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                id="city-quote-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <input type="hidden" name="city_name" value={citySlug || ''} />

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    Full Name <span className="text-[#D97642]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                      <i className="ri-user-line text-[16px] text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Phone <span className="text-[#D97642]">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <i className="ri-phone-line text-[16px] text-gray-400"></i>
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(317) 000-0000"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Email <span className="text-[#D97642]">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <i className="ri-mail-line text-[16px] text-gray-400"></i>
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="jane@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    ZIP Code <span className="text-[#D97642]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                      <i className="ri-map-pin-line text-[16px] text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      name="zip"
                      required
                      placeholder={city?.zipCodes[0] || "46032"}
                      pattern="[0-9]{5}"
                      maxLength={5}
                      value={formData.zip}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    What Are You Looking For?
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                      <i className="ri-shield-line text-[16px] text-gray-400"></i>
                    </div>
                    <select
                      name="insuranceType"
                      value={formData.insuranceType}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] focus:outline-none focus:border-[#D97642] transition-colors cursor-pointer bg-white appearance-none"
                    >
                      <option value="">Not sure — help me decide</option>
                      <option value="term_life">Term Life Insurance</option>
                      <option value="whole_life">Whole Life Insurance</option>
                      <option value="final_expense">Final Expense Insurance</option>
                      <option value="mortgage_protection">Mortgage Protection</option>
                      <option value="annuity">Annuity / Retirement Planning</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-arrow-down-s-line text-[16px] text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                    Best Time to Call
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                      <i className="ri-time-line text-[16px] text-gray-400"></i>
                    </div>
                    <select
                      name="bestTime"
                      value={formData.bestTime}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-[#1A3A52] focus:outline-none focus:border-[#D97642] transition-colors cursor-pointer bg-white appearance-none"
                    >
                      <option value="">Any time</option>
                      <option value="morning">Morning (8am – 12pm)</option>
                      <option value="afternoon">Afternoon (12pm – 5pm)</option>
                      <option value="evening">Evening (5pm – 8pm)</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-arrow-down-s-line text-[16px] text-gray-400"></i>
                    </div>
                  </div>
                </div>

                {submitError && (
                  <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                    <i className="ri-error-warning-line text-[15px]"></i>
                    {submitError}
                  </p>
                )}

                {/* SMS Consent */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="sms_consent"
                      required
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer"
                    />
                    <span className="text-[12px] text-gray-600 leading-relaxed">
                      <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance regarding my quote request and insurance information. Message &amp; data rates may apply. Reply STOP to opt out at any time.{' '}
                      <a href="/privacy-policy" className="text-[#D97642] underline hover:no-underline">Privacy Policy</a>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !smsConsent}
                  className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-[18px]"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-[16px]"></i>
                      Get My Free Quote Now
                    </>
                  )}
                </button>

                <p className="text-[11px] text-[#718096] text-center leading-[1.6]">
                  By submitting, you agree to be contacted by Hoosier Life Insurance. We never sell your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-4">
              Frequently Asked Questions — {city?.name}
            </h2>
            <p className="text-[16px] text-[#4A5568]">
              Common questions from {city?.name} residents about life insurance, costs, and our services.
            </p>
          </div>

          <div className="space-y-4">
            {city?.faqs.map((faq, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden border transition-all duration-200 ${
                  openFaqIndex === index
                    ? 'border-[#D97642]/60 bg-[#FDF6F0] shadow-md'
                    : 'border-gray-200 bg-white hover:border-[#D97642]/30'
                }`}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer group"
                >
                  <div className="flex items-start gap-3 pr-3">
                    <span className={`mt-0.5 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-[11px] font-bold transition-colors ${
                      openFaqIndex === index ? 'bg-[#D97642] text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-[#D97642]/20'
                    }`}>
                      Q
                    </span>
                    <span className="text-[16px] font-semibold text-[#1A3A52] leading-snug">
                      {faq.question}
                    </span>
                  </div>
                  <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                    openFaqIndex === index ? 'bg-[#D97642] rotate-180' : 'bg-gray-100 text-gray-600 group-hover:bg-[#D97642]/20'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaqIndex === index ? 'text-white' : 'text-gray-600'}`}></i>
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openFaqIndex === index ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pl-[60px]">
                    <p className="text-[15px] text-[#4A5568] leading-[1.8]">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 bg-gradient-to-br from-[#1A3A52] via-[#2D5A7B] to-[#D97642] rounded-2xl px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-[22px] font-bold text-white leading-snug mb-2">
                Still have questions about {city?.name} life insurance?
              </h3>
              <p className="text-[14px] text-white/80">
                Call us at (317) 648-2983 or book a free consultation — no pressure, just honest answers.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="tel:+13176482983"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#D97642] text-[14px] font-semibold rounded-full hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-[16px]"></i>
                Call Now
              </a>
              <a
                href="/wealth-strategy-call"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/15 text-white text-[14px] font-semibold rounded-full hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-[16px]"></i>
                Book Free Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby Cities Section */}
      {nearby && nearby.length > 0 && (
        <section className="py-12 md:py-16 bg-[#FDF6F0]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
            <h2 className="text-[28px] font-bold text-[#1A3A52] mb-8 text-center">
              We Also Serve Nearby Communities
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {nearby.map((c) => (
                <a
                  key={c.slug}
                  href={`/service-areas/${c.slug}`}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg text-[14px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] hover:shadow-md transition-all cursor-pointer"
                >
                  <i className="ri-map-pin-line text-[16px]"></i>
                  {c.name}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hamilton County Wealth Strategy CTA - Only for Carmel & Fishers */}
      {(citySlug === 'carmel' || citySlug === 'fishers') && (
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#1A3A52] via-[#2D5A7B] to-[#D97642] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-[150px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/10 rounded-full blur-[120px]"></div>
          </div>

          <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              {/* Left — Content */}
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 text-white text-[12px] font-semibold uppercase tracking-widest rounded-full mb-4 border border-white/30">
                  <i className="ri-vip-crown-fill text-[13px]"></i>
                  Hamilton County Exclusive
                </span>
                <h2 className="text-[36px] md:text-[44px] font-bold text-white leading-[1.15] mb-5">
                  Hamilton County's Trusted Retirement & Life Insurance Specialist
                </h2>
                <p className="text-[17px] text-white/85 leading-[1.7] mb-8">
                  {citySlug === 'carmel' 
                    ? "Carmel families are building serious wealth — and you need a financial strategy that protects it. Our Wealth Strategy Call helps high-income professionals coordinate life insurance, retirement income planning, and tax-advantaged wealth building into one cohesive plan."
                    : "Fishers families are in their prime earning years — and this is the perfect time to lock in a comprehensive wealth protection strategy. Our Wealth Strategy Call helps you coordinate life insurance, retirement planning, and long-term wealth building so you never leave money on the table."
                  }
                </p>

                {/* What's Covered Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                      <i className="ri-shield-check-line text-[16px] text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-white mb-1">Life Insurance Strategy</h3>
                      <p className="text-[13px] text-white/75 leading-[1.5]">Term, whole life, IUL — we'll find the right mix for your family</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                      <i className="ri-line-chart-line text-[16px] text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-white mb-1">Retirement Income Gap</h3>
                      <p className="text-[13px] text-white/75 leading-[1.5]">Calculate how much you'll need beyond 401(k) and Social Security</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                      <i className="ri-file-shield-line text-[16px] text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-white mb-1">Tax-Advantaged Growth</h3>
                      <p className="text-[13px] text-white/75 leading-[1.5]">Minimize taxes on retirement withdrawals and legacy transfers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                      <i className="ri-building-line text-[16px] text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold text-white mb-1">Carrier Comparison</h3>
                      <p className="text-[13px] text-white/75 leading-[1.5]">We shop 15+ A-rated insurers to find your best rate</p>
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3 mb-8">
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                    <i className="ri-check-line text-[15px] text-[#D97642]"></i>
                    <span className="text-[13px] text-white/90 font-medium">No pressure</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                    <i className="ri-time-line text-[15px] text-[#D97642]"></i>
                    <span className="text-[13px] text-white/90 font-medium">Free 30-min call</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                    <i className="ri-map-pin-line text-[15px] text-[#D97642]"></i>
                    <span className="text-[13px] text-white/90 font-medium">Local {city?.name} specialist</span>
                  </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="/wealth-strategy-call"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#D97642] text-white text-[15px] font-semibold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-xl shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line text-[18px]"></i>
                    Book Your Free Wealth Strategy Call
                  </a>
                  <a
                    href="tel:+13176482983"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/15 text-white text-[15px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-[18px]"></i>
                    Or call (317) 648-2983
                  </a>
                </div>
              </div>

              {/* Right — Specialist Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1A3A52] to-[#2D5A7B] rounded-full flex items-center justify-center text-white text-[22px] font-bold flex-shrink-0">
                    BW
                  </div>
                  <div>
                    <h3 className="text-[20px] font-bold text-[#1A3A52] mb-1">Benjamin Wright</h3>
                    <p className="text-[14px] text-[#4A5568] mb-2">Retirement Income Specialist</p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((star) => (
                        <i key={star} className="ri-star-fill text-[14px] text-yellow-400"></i>
                      ))}
                      <span className="text-[13px] text-[#4A5568] ml-1.5">4.9/5 · 89 reviews</span>
                    </div>
                  </div>
                </div>

                <p className="text-[15px] text-[#4A5568] leading-[1.7] mb-6 italic">
                  "I've helped over 300 Hamilton County families build comprehensive wealth protection strategies. Most high-income professionals are leaving money on the table because they don't know how to coordinate life insurance with retirement planning. Let's fix that."
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-[16px] text-green-600"></i>
                    <span className="text-[14px] text-[#4A5568]">No obligation — just honest advice</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-[16px] text-green-600"></i>
                    <span className="text-[14px] text-[#4A5568]">100% free consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-[16px] text-green-600"></i>
                    <span className="text-[14px] text-[#4A5568]">Same-day availability</span>
                  </div>
                </div>

                <div className="bg-[#FDF6F0] rounded-lg p-4 border border-[#D97642]/20">
                  <p className="text-[13px] text-[#4A5568] leading-[1.6]">
                    <strong className="text-[#1A3A52]">What to expect:</strong> Calls typically last 30 minutes. We'll send you a custom retirement income proposal within 24 hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
